(function () {
  'use strict';

  const shared = window.VeloForgeConfiguratorRuntime || {
    makeCache: window.VeloForgeConfiguratorContract?.createRetryableCache(),
    displayNameLookupCache: window.VeloForgeConfiguratorContract?.createRetryableCache(),
    paintIndexCache: window.VeloForgeConfiguratorContract?.createRetryableCache(),
    instances: new WeakMap(),
    initialProductViews: new Set(),
    mobilePreviewUpdaters: new Set(),
    pdfInstances: new WeakMap(),
    viewportLifecycleInstalled: false,
    lifecycleInstalled: false,
  };
  if (!shared.makeCache?.isRetryableResourceCache) shared.makeCache = window.VeloForgeConfiguratorContract?.createRetryableCache();
  if (!shared.displayNameLookupCache?.isRetryableResourceCache) shared.displayNameLookupCache = window.VeloForgeConfiguratorContract?.createRetryableCache();
  if (!shared.paintIndexCache?.isRetryableResourceCache) shared.paintIndexCache = window.VeloForgeConfiguratorContract?.createRetryableCache();
  if (!(shared.initialProductViews instanceof Set)) shared.initialProductViews = new Set();
  if (!(shared.mobilePreviewUpdaters instanceof Set)) shared.mobilePreviewUpdaters = new Set();
  if (!(shared.pdfInstances instanceof WeakMap)) shared.pdfInstances = new WeakMap();
  window.VeloForgeConfiguratorRuntime = shared;

  if (!shared.viewportLifecycleInstalled) {
    shared.viewportLifecycleInstalled = true;
    const updateMobilePreviews = () => shared.mobilePreviewUpdaters.forEach(update => update());
    window.addEventListener('scroll', updateMobilePreviews, { passive: true });
    window.addEventListener('resize', updateMobilePreviews, { passive: true });
  }

  function initializeConfigurator(root) {
    if (!(root instanceof HTMLElement) || root.dataset.vfInitialized === 'true') return;
    root.dataset.vfInitialized = 'true';
    const lifecycleController = new AbortController();
    const lifecycleSignal = lifecycleController.signal;
    let destroyed = false;
    let mobilePreviewFrame = 0;
    let mobileUtilityHeightObserver = null;
    let statusTimeout = 0;
    let legacyViewportListener = null;
    let legacyYearViewportListener = null;
    const rootTimers = new Set();
    const requestCoordinator = window.VeloForgeConfiguratorContract?.createRequestCoordinator();
    const productEventCoordinator = window.VeloForgeConfiguratorContract?.createProductEventCoordinator(
      shared.initialProductViews,
      root.dataset.configuratorInstance || root.dataset.contextKey || 'configurator'
    );
    if (!requestCoordinator || !productEventCoordinator || !shared.makeCache || !shared.displayNameLookupCache || !shared.paintIndexCache) {
      console.error('VeloForge: configurator support could not be initialized.');
      root.dataset.vfInitialized = 'false';
      return;
    }
    shared.instances.set(root, {
      destroy() {
        if (destroyed) return;
        destroyed = true;
        requestCoordinator.destroy();
        productEventCoordinator.destroy();
        rootTimers.forEach(timer => window.clearTimeout(timer));
        rootTimers.clear();
        if (mobilePreviewFrame) window.cancelAnimationFrame(mobilePreviewFrame);
        mobileUtilityHeightObserver?.disconnect();
        shared.mobilePreviewUpdaters.delete(scheduleMobilePreviewUpdate);
        shared.pdfInstances.delete(root);
        legacyViewportListener?.media?.removeListener?.(legacyViewportListener.listener);
        legacyYearViewportListener?.media?.removeListener?.(legacyYearViewportListener.listener);
        lifecycleController.abort();
        shared.instances.delete(root);
        delete root.dataset.vfInitialized;
      },
    });

  /* ===== DOM REFS ===== */
  const form = root.querySelector('form[data-configurator-form]');
  const makeSel = root.querySelector('[data-select="make"]');
  const yearSel = root.querySelector('[data-select="year"]');
  const makeComboInput = root.querySelector('[data-combobox-input="make"]');
  const yearComboInput = root.querySelector('[data-combobox-input="year"]');
  const makeComboList = root.querySelector('[data-combobox-list="make"]');
  const makeLabel = root.querySelector('[data-make-label]');
  const makeSummary = root.querySelector('[data-make-summary]');
  const makeEditButton = root.querySelector('[data-make-edit]');
  const yearComboList = root.querySelector('[data-combobox-list="year"]');
  const secondaryLabel = root.querySelector('[data-secondary-label]');
  const vehicleHelper = root.querySelector('[data-vehicle-helper]');
  const paintGrid = root.querySelector('[data-paint-grid]');
  const paintUnavailable = root.querySelector('[data-paint-unavailable]');
  const inputPaintName = root.querySelector('[data-input="paint-name"]');
  const paintSearchList = root.querySelector('[data-paint-search-list]');
  const inputPaintCode = root.querySelector('[data-input="paint-code"]');
  const inputPaintHex = root.querySelector('[data-input="paint-hex"]');
  const sizeGrid = root.querySelector('[data-size-grid]');
  const variantHidden = root.querySelector('[data-variant-id]');
  const summaryPaint = root.querySelector('[data-summary-paint]');
  const summaryVehicle = root.querySelector('[data-summary-vehicle]');
  const propSeries = root.querySelector('[data-prop="Series"]');
  const propMetal = root.querySelector('[data-prop="Metal"]');
  const propKarat = root.querySelector('[data-prop="Karat"]');
  const propSize = root.querySelector('[data-prop="Size"]');
  const propPaintName = root.querySelector('[data-prop="Paint Name"]');
  const propPaintCode = root.querySelector('[data-prop="Paint Code"]');
  const propHex = root.querySelector('[data-prop="Hex"]');
  const propPreviewImage = root.querySelector('[data-prop="Preview Image"]');
  const propConfigurationUrl = root.querySelector('[data-prop="Configuration URL"]');
  const propOtherDetails = root.querySelector('[data-prop="Other Details"]');
  const propVehicle = root.querySelector('[data-prop="Vehicle"]');
  const propModel = root.querySelector('[data-prop="Model"]');
  const propNotes = root.querySelector('[data-prop="Notes"]');
  const propNotesFallback = root.querySelector('[data-prop="Notes Fallback"]');
  const neutralBtn = root.querySelector('[data-neutral-preview]');
  const qtyInput = root.querySelector('[data-quantity-input]');
  const qtyValue = root.querySelector('[data-quantity-value]');
  const decreaseBtn = root.querySelector('[data-quantity-decrease]');
  const increaseBtn = root.querySelector('[data-quantity-increase]');
  const addBtn = root.querySelector('[data-add-to-cart]');
  const statusEl = root.querySelector('[data-status]');
  const paintAssetMapElement = root.querySelector('[data-paint-asset-map]');
  let paintAssetMap = {};
  try {
    paintAssetMap = JSON.parse(paintAssetMapElement?.textContent || '{}');
  } catch (error) {
    warnInDevelopmentOnce('paint-asset-map', 'The versioned paint asset map could not be read; local asset fallbacks will be used.');
  }

  /* Desktop gallery */
  const desktopGallery = root.querySelector('[data-desktop-gallery]');
  let gallerySlides = Array.from(root.querySelectorAll('[data-gallery-slide]'));
  const galleryPrevious = root.querySelector('[data-gallery-previous]');
  const galleryNext = root.querySelector('[data-gallery-next]');

  /* Mobile floating preview */
  const mobileGallery = root.querySelector('[data-mobile-gallery]');
  const mobilePreviewShell = root.querySelector('[data-mobile-preview-shell]');
  const mobilePreviewReturn = root.querySelector('[data-mobile-preview-return]');
  const mobilePreviewMove = root.querySelector('[data-mobile-preview-move]');
  const mobilePreviewArrow = root.querySelector('[data-mobile-preview-arrow]');
  const mobileUtilityBar = root.closest('[data-vehicle-make-landing]')?.querySelector('[data-vf-make-utility]');
  const usesBoundedMobilePreview = ['vehicle_make', 'ring_product'].includes(root.dataset.configuratorContext);

  /* Ring preview layers (desktop + mobile) */
  const cwDesktop = root.querySelector('[data-ring-color-wash]');
  const tDesktop = root.querySelector('[data-ring-tint]');
  const hDesktop = root.querySelector('[data-ring-highlight]');
  const cwMobile = root.querySelector('[data-ring-color-wash-m]');
  const tMobile = root.querySelector('[data-ring-tint-m]');
  const hMobile = root.querySelector('[data-ring-highlight-m]');
  const ringInlayMask = root.dataset.ringInlay;
  const ringBaseImages = [root.querySelector('[data-ring-base-img]'), root.querySelector('[data-ring-base-img-mobile]')].filter(Boolean);
  const productTitle = root.querySelector('[data-product-title]');
  const productPrice = root.querySelector('[data-product-price]');
  const productPricePrefix = root.querySelector('[data-product-price-prefix]');
  const builderEyebrow = root.querySelector('[data-builder-eyebrow]');
  const builderSubtitle = root.querySelector('[data-builder-subtitle]');
  const previewOverlays = Array.from(root.querySelectorAll('[data-preview-overlay]'));
  const previewInstagramButtons = Array.from(root.querySelectorAll('[data-preview-instagram]'));
  const previewFacebookButtons = Array.from(root.querySelectorAll('[data-preview-facebook]'));
  const previewDownloadButtons = Array.from(root.querySelectorAll('[data-preview-download]'));
  const previewShareButtons = Array.from(root.querySelectorAll('[data-preview-share]'));
  const previewActionStatuses = Array.from(root.querySelectorAll('[data-preview-action-status]'));
  let previewActionStatusTimer = 0;
  if (root.dataset.shareAssetsAvailable !== 'true') {
    [...previewInstagramButtons, ...previewDownloadButtons].forEach(button => {
      button.setAttribute('aria-disabled', 'true');
      button.title = `${root.dataset.ringSeriesName || 'This Series'} artwork coming soon`;
    });
  }
  const vehicleMakeIntroduction = root.closest('[data-vehicle-make-landing]')
    ?.querySelector('[data-vehicle-make-configurator-introduction]');

  /* Notes UI refs */
  const notesOpenBtn = root.querySelector('[data-notes-open]');
  const notesForm = root.querySelector('[data-notes-form]');
  const notesMakeInput = root.querySelector('[data-notes-make]');
  const notesYearInput = root.querySelector('[data-notes-year]');
  const notesPaintNameInput = root.querySelector('[data-notes-paint-name]');
  const notesPaintCodeInput = root.querySelector('[data-notes-paint-code]');
  const notesOtherDetailsInput = root.querySelector('[data-notes-other-details]');
  const notesSaveBtn = root.querySelector('[data-notes-save]');
  const notesCancelBtn = root.querySelector('[data-notes-cancel]');
  const notesPreview = root.querySelector('[data-notes-preview]');
  const notesPreviewText = root.querySelector('[data-notes-preview-text]');
  const notesEditBtn = root.querySelector('[data-notes-edit]');
  const notesDeleteBtn = root.querySelector('[data-notes-delete]');

  /* ===== STATE ===== */
  /** Shared retryable cache of paint-source slug → validated data promise */
  const makeCache = shared.makeCache;
  /** Shared retryable cache of optional reviewed display-name lookup promises */
  const displayNameLookupCache = shared.displayNameLookupCache;
  const developmentWarnings = new Set();
  let paintRegistry = { makes: [], technicalPaintSources: [], specialPaintPrograms: {} };
  let selectedPaint = null;
  let makePaintSearchCatalog = [];
  let paintSearchActiveIndex = -1;
  let selectedSize = null;
  let qty = 1;
  let activeGalleryIndex = 0;
  let registryLoaded = false;
  let registryRetryPromise = null;
  let initialRestoreComplete = false;
  let applyingUrlState = false;

  let savedNotesData = null;
  let savedNotes = '';

  /* ===== DESKTOP GALLERY ===== */
  function showGallerySlide(index) {
    if (!desktopGallery || !gallerySlides.length) return;

    const nextIndex = Math.max(0, Math.min(Number(index) || 0, gallerySlides.length - 1));
    activeGalleryIndex = nextIndex;

    gallerySlides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === activeGalleryIndex;
      slide.hidden = !isActive;
      slide.setAttribute('aria-hidden', String(!isActive));
    });

    if (galleryPrevious) galleryPrevious.disabled = activeGalleryIndex === 0;
    if (galleryNext) galleryNext.disabled = activeGalleryIndex === gallerySlides.length - 1;
  }

  galleryPrevious?.addEventListener('click', () => showGallerySlide(activeGalleryIndex - 1), { signal: lifecycleSignal });
  galleryNext?.addEventListener('click', () => showGallerySlide(activeGalleryIndex + 1), { signal: lifecycleSignal });

  showGallerySlide(0);

  /* ===== MOBILE FLOATING PREVIEW ===== */
  const MOBILE_PREVIEW_SIDE_KEY = 'vf_mobile_preview_side';
  const mobileViewport = window.matchMedia('(max-width: 1024px)');
  const syncYearControlForViewport = () => {
    const useNativeYearSelect = mobileViewport.matches;
    if (yearSel) {
      yearSel.tabIndex = useNativeYearSelect ? 0 : -1;
      yearSel.setAttribute('aria-hidden', String(!useNativeYearSelect));
    }
    if (yearComboInput) {
      yearComboInput.tabIndex = useNativeYearSelect ? -1 : 0;
      yearComboInput.setAttribute('aria-hidden', String(useNativeYearSelect));
    }
    if (useNativeYearSelect) closeCombobox('year');
  };

  syncYearControlForViewport();
  if (mobileViewport.addEventListener) {
    mobileViewport.addEventListener('change', syncYearControlForViewport, { signal: lifecycleSignal });
  } else {
    mobileViewport.addListener?.(syncYearControlForViewport);
    legacyYearViewportListener = { media: mobileViewport, listener: syncYearControlForViewport };
  }

  try {
    const savedSide = sessionStorage.getItem(MOBILE_PREVIEW_SIDE_KEY);
    if (savedSide === 'left' || savedSide === 'right') {
      mobilePreviewShell?.setAttribute('data-preview-side', savedSide);
    }
  } catch (_) {}

  function updateMobilePreviewDirection() {
    if (!mobilePreviewShell || !mobilePreviewMove || !mobilePreviewArrow) return;
    const destination = mobilePreviewShell.dataset.previewSide === 'left' ? 'right' : 'left';
    mobilePreviewMove.setAttribute('aria-label', `Move ring preview to the ${destination}`);
    mobilePreviewArrow.setAttribute(
      'd',
      destination === 'left' ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6'
    );
  }

  updateMobilePreviewDirection();

  function setMobilePreviewFloating(shouldFloat) {
    if (!mobilePreviewShell) return;
    const isFloating = Boolean(!usesBoundedMobilePreview && shouldFloat && mobileViewport.matches);
    mobilePreviewShell.classList.toggle('vf__mobile-preview-shell--floating', isFloating);
  }

  function updateMobilePreviewPosition() {
    if (!mobileGallery || !mobileViewport.matches) {
      setMobilePreviewFloating(false);
      return;
    }

    const galleryBounds = mobileGallery.getBoundingClientRect();
    const headerHeight = Number.parseFloat(
      getComputedStyle(document.body).getPropertyValue('--header-height')
    ) || 0;
    setMobilePreviewFloating(galleryBounds.bottom <= headerHeight);
  }

  function scheduleMobilePreviewUpdate() {
    if (destroyed || mobilePreviewFrame) return;
    mobilePreviewFrame = window.requestAnimationFrame(() => {
      mobilePreviewFrame = 0;
      if (destroyed || !root.isConnected) return;
      updateMobilePreviewPosition();
    });
  }

  if (!usesBoundedMobilePreview) {
    shared.mobilePreviewUpdaters.add(scheduleMobilePreviewUpdate);
    if (mobileViewport.addEventListener) {
      mobileViewport.addEventListener('change', scheduleMobilePreviewUpdate, { signal: lifecycleSignal });
    } else {
      mobileViewport.addListener?.(scheduleMobilePreviewUpdate);
      legacyViewportListener = { media: mobileViewport, listener: scheduleMobilePreviewUpdate };
    }
    mobilePreviewMove?.addEventListener('click', (event) => {
      event.stopPropagation();
      if (!mobilePreviewShell) return;
      const nextSide = mobilePreviewShell.dataset.previewSide === 'left' ? 'right' : 'left';
      mobilePreviewShell.dataset.previewSide = nextSide;
      updateMobilePreviewDirection();
      try { sessionStorage.setItem(MOBILE_PREVIEW_SIDE_KEY, nextSide); } catch (_) {}
    }, { signal: lifecycleSignal });
  }

  function returnToFullMobilePreview() {
    if (!mobilePreviewShell?.classList.contains('vf__mobile-preview-shell--floating')) return;
    mobileGallery?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  if (!usesBoundedMobilePreview) {
    mobilePreviewReturn?.addEventListener('click', (event) => {
      event.stopPropagation();
      returnToFullMobilePreview();
    }, { signal: lifecycleSignal });

    mobilePreviewShell?.addEventListener('click', (event) => {
      if (event.target.closest('button')) return;
      returnToFullMobilePreview();
    }, { signal: lifecycleSignal });

    updateMobilePreviewPosition();
  }

  if (usesBoundedMobilePreview && mobileUtilityBar) {
    const syncMobileUtilityHeight = () => {
      root.style.setProperty('--vf-mobile-utility-height', `${Math.ceil(mobileUtilityBar.getBoundingClientRect().height)}px`);
    };
    syncMobileUtilityHeight();
    if ('ResizeObserver' in window) {
      mobileUtilityHeightObserver = new ResizeObserver(syncMobileUtilityHeight);
      mobileUtilityHeightObserver.observe(mobileUtilityBar);
    }
  }

  const contextKey = (root.dataset.contextKey || root.dataset.vfRootId || 'default').replace(/[^a-z0-9_-]+/gi, '-');
  const STATE_KEY = `vf_configurator_state_v4:${contextKey}`;
  const LEGACY_STATE_KEY = `vf_configurator_state_v3:${contextKey}`;
  const LEGACY_NOTES_KEY = `vf_configurator_notes_v3:${contextKey}`;
  const defaultMake = root.dataset.defaultMake || '';
  const lockMake = root.dataset.lockMake === 'true';
  const makeCombobox = root.querySelector('[data-combobox="make"]');
  const lockedMake = root.querySelector('[data-locked-make]');
  const lockedMakeValue = root.querySelector('[data-locked-make-value]');
  let editingMake = false;

  /* Variant map from inline JSON script tag */
  let variantMap = JSON.parse(root.querySelector('[data-variant-map]')?.textContent || '{}');
  const ringConfigurations = JSON.parse(root.querySelector('[data-ring-configurations]')?.textContent || '[]');
  let activeConfiguration = ringConfigurations.find(configuration => configuration.id === root.dataset.activeConfiguration) || ringConfigurations[0] || null;
  let selectedOptionsVariantResolver = activeConfiguration?.merchandiseArchitecture === 'series-product'
    ? window.VeloForgeConfiguratorContract.createSelectedOptionsVariantResolver(activeConfiguration.product)
    : null;
  const renderedMetalSelection = root.querySelector('[data-configuration-metal][aria-checked="true"]')?.dataset.configurationMetal || '';
  const renderedKaratSelection = root.querySelector('[data-configuration-karat][aria-checked="true"]')?.dataset.configurationKarat || '';
  const renderedConfiguration = ringConfigurations.find(configuration =>
    configuration.product &&
    configuration.metalKey === renderedMetalSelection &&
    configuration.karatKey === renderedKaratSelection
  );
  const renderedSelectionMatchesActive = Boolean(
    renderedConfiguration && activeConfiguration && renderedConfiguration.id === activeConfiguration.id
  );
  const nativeAnalyticsProducts = ringConfigurations.map(configuration => configuration?.product?.nativeAnalyticsProduct);
  const registerNativeCartAnalyticsProducts = () =>
    window.VeloForgeConfiguratorContract.registerNativeCartAnalyticsProducts(nativeAnalyticsProducts);
  if (!registerNativeCartAnalyticsProducts()) {
    window.addEventListener('load', registerNativeCartAnalyticsProducts, { once: true, signal: lifecycleSignal });
  }
  let intentionalMetalSelected = renderedSelectionMatchesActive;
  let intentionalKaratSelected = renderedSelectionMatchesActive;
  const configuratorContext = root.dataset.configuratorContext || 'product';
  const urlStateEnabled = ['vehicle_make', 'ring_product'].includes(configuratorContext);
  const usesInPlaceConfiguration = ['vehicle_make', 'ring_product'].includes(configuratorContext) && Boolean(activeConfiguration);
  const emitsInitialBackingProductView = configuratorContext === 'vehicle_make' && usesInPlaceConfiguration;
  const backingVariantIndex = window.VeloForgeConfiguratorContract.createBackingVariantIndex(ringConfigurations);
  const allowedBackingVariantIds = new Set(
    ringConfigurations.length
      ? ringConfigurations.flatMap(configuration => (configuration?.product?.variants || []).map(variant => String(variant.id)))
      : Object.values(variantMap).map(String)
  );
  const optionIdPrefix = root.dataset.configuratorInstance || root.dataset.vfRootId || 'vf-configurator';

  if (root.dataset.configuratorContext === 'product' && window.location.search) {
    root.querySelectorAll('a.vf-option-btn[href]').forEach(link => {
      const destination = new URL(link.href, window.location.origin);
      const currentParameters = new URLSearchParams(window.location.search);
      currentParameters.forEach((value, key) => {
        if (!destination.searchParams.has(key)) destination.searchParams.append(key, value);
      });
      link.href = destination.toString();
    });
  }

  function syncRadioGroup(group, selectedItem = null) {
    if (!group) return;
    const items = Array.from(group.querySelectorAll('[role="radio"]'));
    const enabledItems = items.filter(item => !item.disabled && item.getAttribute('aria-disabled') !== 'true');
    const checkedItem = selectedItem || items.find(item => item.getAttribute('aria-checked') === 'true');
    items.forEach(item => {
      const checked = item === checkedItem;
      item.setAttribute('aria-checked', String(checked));
      item.tabIndex = checked ? 0 : -1;
    });
    if (!checkedItem && enabledItems[0]) enabledItems[0].tabIndex = 0;
  }

  function setupRadioGroup(group) {
    if (!group) return;
    syncRadioGroup(group);
    group.addEventListener('keydown', event => {
      if ((event.key === ' ' || event.key === 'Spacebar') && event.target.matches('a[role="radio"]')) {
        event.preventDefault();
        event.target.click();
        return;
      }
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      const items = Array.from(group.querySelectorAll('[role="radio"]'))
        .filter(item => !item.disabled && item.getAttribute('aria-disabled') !== 'true');
      if (!items.length) return;
      const currentIndex = Math.max(0, items.indexOf(document.activeElement));
      let nextIndex = currentIndex;
      if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = items.length - 1;
      else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % items.length;
      else nextIndex = (currentIndex - 1 + items.length) % items.length;
      event.preventDefault();
      items[nextIndex].focus();
      items[nextIndex].click();
    }, { signal: lifecycleSignal });
  }

  root.querySelectorAll('[role="radiogroup"]').forEach(setupRadioGroup);

  function withStandardEvents(callback) {
    if (!usesInPlaceConfiguration || !requestCoordinator.isActive()) return;
    const standardEvents = window.Shopify?.VeloForgeStandardEvents;
    if (standardEvents) {
      callback(standardEvents);
      return;
    }
    document.addEventListener('velo-forge:standard-events:ready', () => {
      if (requestCoordinator.isActive() && window.Shopify?.VeloForgeStandardEvents) {
        callback(window.Shopify.VeloForgeStandardEvents);
      }
    }, { once: true, signal: lifecycleSignal });
  }

  function selectedEventVariant(product) {
    if (!selectedSize) return null;
    return product?.variants?.find(variant => variant.title === selectedSize)?.eventVariant || null;
  }

  function productViewPayload(product) {
    if (!product?.viewEvent) return null;
    const payload = JSON.parse(JSON.stringify(product.viewEvent));
    const variant = selectedEventVariant(product);
    if (variant) {
      payload.product.selectedVariant = variant;
      payload.selectedOptions = variant.selectedOptions || [];
    }
    return payload;
  }

  function emitInitialProductView() {
    const product = activeConfiguration?.product;
    const payload = productViewPayload(product);
    if (!payload || !productEventCoordinator.claimInitial(product.id)) return;
    withStandardEvents(standardEvents => standardEvents.productView(root, payload));
  }

  function emitProductChange(product) {
    const payload = productViewPayload(product);
    if (!payload || !productEventCoordinator.claimProductChange(product.id)) return;
    withStandardEvents(standardEvents => standardEvents.productView(root, payload));
  }

  function emitVariantChange(product, variant) {
    if (!variant?.eventVariant || !productEventCoordinator.claimVariantChange(variant.id)) return;
    const selectedOptions = variant.eventVariant.selectedOptions || [];
    withStandardEvents(standardEvents => standardEvents.productSelect(form || root, {
      selectedOptions,
      product: {
        id: product.id,
        title: product.title,
        handle: product.handle,
      },
    }, variant.eventVariant));
  }

  if (emitsInitialBackingProductView) emitInitialProductView();

  /* ===== VEHICLE DATA ===== */

  /** Same slug logic as split-paint-data.js */
  function makeSlug(name) {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function getSpecialPaintPrograms(make) {
    const programs = paintRegistry.specialPaintPrograms?.[make];
    return Array.isArray(programs) ? programs : [];
  }

  function formatProgramLabels(programs) {
    const labels = programs.map(program => program.label).filter(Boolean);
    if (labels.length < 2) return labels[0] || '';
    if (labels.length === 2) return labels.join(' or ');
    return `${labels.slice(0, -1).join(', ')}, or ${labels.at(-1)}`;
  }

  function updateVehicleMakeIntroduction() {
    if (!vehicleMakeIntroduction || !lockMake) return;
    const programs = getSpecialPaintPrograms(defaultMake);
    const programLabels = formatProgramLabels(programs);
    vehicleMakeIntroduction.textContent = programLabels
      ? `Choose ${programLabels} or your vehicle year, then select your factory paint color and build your ring.`
      : vehicleMakeIntroduction.dataset.defaultCopy;
  }

  function getSelectedPaintProgram(make = makeSel?.value || '', value = yearSel?.value || '') {
    return getSpecialPaintPrograms(make).find(program => program.value === value) || null;
  }

  function configurationUrlState() {
    return window.VeloForgeConfiguratorContract.readConfigurationUrl(window.location.search);
  }

  function buildCurrentConfigurationUrl() {
    const program = getSelectedPaintProgram();
    const shareablePaint = selectedPaint?.shareable === true ? selectedPaint : null;
    const configurationComplete = isConfigurationSelectionComplete();
    return window.VeloForgeConfiguratorContract.buildConfigurationUrl(window.location.href, {
      series: root.dataset.ringSeriesHandle || '',
      metal: configurationComplete ? activeConfiguration?.metalKey : '',
      karat: configurationComplete ? activeConfiguration?.karatKey : '',
      size: selectedSize,
      make: makeSel?.value || '',
      lockMake,
      year: program ? '' : yearSel?.value || '',
      program: program?.value || '',
      paint: shareablePaint ? makeSlug(shareablePaint.name) : '',
      paintCode: shareablePaint?.code || '',
      quantity: qty,
    });
  }

  function syncConfigurationUrl() {
    if (!urlStateEnabled || !initialRestoreComplete || applyingUrlState || !window.history?.replaceState) return;
    const nextUrl = buildCurrentConfigurationUrl();
    window.history.replaceState(window.history.state, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
  }

  function currentVehicleMake() {
    return String(makeSel?.value || root.dataset.vehicleMakeDisplay || '').trim();
  }

  function currentVehicleYear() {
    const selectedOption = yearSel?.options?.[yearSel.selectedIndex];
    return yearSel?.value && selectedOption ? String(selectedOption.textContent || '').trim() : '';
  }

  let mobileOverlayLayoutFrame = 0;
  let builderSubtitleLayoutFrame = 0;
  let builderEyebrowLayoutFrame = 0;

  function visibleTextEdge(text, font, edge = 'left') {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context || !text) return null;
    const fontSize = Number.parseFloat(font) || 28;
    const padding = Math.ceil(fontSize * 2);
    context.font = font;
    const metrics = context.measureText(text);
    canvas.width = Math.ceil(metrics.width + padding * 2);
    canvas.height = Math.ceil(fontSize * 3);
    context.font = font;
    context.fillStyle = '#000';
    context.textBaseline = 'alphabetic';
    const baseline = Math.ceil(fontSize * 1.8);
    context.fillText(text, padding, baseline);
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
    const bandTop = Math.max(0, Math.floor(baseline - fontSize * 0.62));
    const bandBottom = Math.min(canvas.height, Math.ceil(baseline + fontSize * 0.04));
    let found = edge === 'left' ? canvas.width : -1;
    for (let y = bandTop; y < bandBottom; y += 1) {
      for (let x = 0; x < canvas.width; x += 1) {
        if (pixels[(y * canvas.width + x) * 4 + 3] < 24) continue;
        found = edge === 'left' ? Math.min(found, x) : Math.max(found, x);
      }
    }
    if (found === canvas.width || found < 0) return null;
    return found - padding;
  }

  function visibleCapitalDownstroke(text, font) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context || !text) return null;
    const fontSize = Number.parseFloat(font) || 28;
    const padding = Math.ceil(fontSize * 2);
    context.font = font;
    const metrics = context.measureText(text.charAt(0));
    canvas.width = Math.ceil(metrics.width + padding * 2);
    canvas.height = Math.ceil(fontSize * 3);
    context.font = font;
    context.fillStyle = '#000';
    context.textBaseline = 'alphabetic';
    const baseline = Math.ceil(fontSize * 1.8);
    context.fillText(text.charAt(0), padding, baseline);
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
    // Amsterdam Four's capital M has a wide upper-left flourish. The first
    // structural downstroke is isolated near the baseline, below that flourish.
    const bandTop = Math.max(0, Math.floor(baseline - fontSize * 0.28));
    const bandBottom = Math.min(canvas.height, Math.ceil(baseline - fontSize * 0.02));
    for (let x = 0; x < canvas.width; x += 1) {
      for (let y = bandTop; y < bandBottom; y += 1) {
        if (pixels[(y * canvas.width + x) * 4 + 3] >= 24) return x - padding;
      }
    }
    return null;
  }

  function applyBuilderSubtitleOpticalGap(make) {
    if (!builderSubtitle || !make) return;
    const makeElements = builderSubtitle.querySelectorAll('.vf__subtitle-make');
    if (!makeElements.length) return;
    const styles = getComputedStyle(builderSubtitle);
    const font = `${styles.fontStyle} ${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;
    const context = document.createElement('canvas').getContext('2d');
    if (!context) return;
    context.font = font;
    const prefix = 'Build your';
    const prefixAdvance = context.measureText(prefix).width;
    const prefixInkRight = visibleTextEdge(prefix, font, 'right');
    const makeBodyLeft = /^M/i.test(make)
      ? visibleCapitalDownstroke(make, font) ?? visibleTextEdge(make, font, 'left')
      : visibleTextEdge(make, font, 'left');
    if (prefixInkRight === null || makeBodyLeft === null) return;
    const desiredGap = Number.parseFloat(styles.fontSize) * 0.08;
    const opticalMargin = prefixInkRight + desiredGap - makeBodyLeft - prefixAdvance;
    makeElements.forEach(makeElement => {
      makeElement.style.setProperty('--vf-subtitle-optical-gap', `${opticalMargin}px`);
    });

    const hyphenatedBackboneMatch = make.match(/^(.*-)(B.*)$/i);
    if (hyphenatedBackboneMatch) {
      const [, makePrefix, makeSuffix] = hyphenatedBackboneMatch;
      context.font = font;
      const makePrefixAdvance = context.measureText(makePrefix).width;
      const makePrefixInkRight = visibleTextEdge(makePrefix, font, 'right');
      const suffixBackboneLeft = visibleCapitalDownstroke(makeSuffix, font);
      if (makePrefixInkRight !== null && suffixBackboneLeft !== null) {
        const segmentGap = Number.parseFloat(styles.fontSize) * 0.04;
        const segmentMargin = makePrefixInkRight + segmentGap - suffixBackboneLeft - makePrefixAdvance;
        makeElements.forEach(makeElement => {
          const suffixElement = Object.assign(document.createElement('span'), {
            className: 'vf__subtitle-make-segment',
            textContent: makeSuffix,
          });
          suffixElement.style.setProperty('--vf-subtitle-segment-optical-gap', `${segmentMargin}px`);
          makeElement.replaceChildren(document.createTextNode(makePrefix), suffixElement);
        });
      }
    }
  }

  function fitBuilderEyebrow() {
    if (!builderEyebrow) return;
    cancelAnimationFrame(builderEyebrowLayoutFrame);
    builderEyebrowLayoutFrame = requestAnimationFrame(() => {
      builderEyebrow.style.removeProperty('font-size');
      const availableWidth = builderEyebrow.clientWidth;
      const requiredWidth = builderEyebrow.scrollWidth;
      if (!availableWidth || requiredWidth <= availableWidth) return;
      const maximumSize = 0.6875 * (Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16);
      builderEyebrow.style.fontSize = `${maximumSize * (availableWidth / requiredWidth)}px`;
    });
  }

  function fitBuilderSubtitle() {
    if (!builderSubtitle) return;
    cancelAnimationFrame(builderSubtitleLayoutFrame);
    builderSubtitleLayoutFrame = requestAnimationFrame(() => {
      builderSubtitle.style.removeProperty('font-size');
      const makeElement = builderSubtitle.querySelector('[data-builder-subtitle-make]');
      if (makeElement) applyBuilderSubtitleOpticalGap(makeElement.textContent.trim());
      const flourishBuffer = 12;
      const availableWidth = Math.max(0, builderSubtitle.clientWidth - flourishBuffer);
      const requiredWidth = builderSubtitle.scrollWidth;
      if (!availableWidth || requiredWidth <= availableWidth) return;
      const maximumSize = Number.parseFloat(getComputedStyle(builderSubtitle).fontSize) || 28;
      builderSubtitle.style.fontSize = `${maximumSize * (availableWidth / requiredWidth)}px`;
    });
  }

  function animateBuilderSubtitleMetal(metal) {
    if (!builderSubtitle || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const subtitleText = builderSubtitle.querySelector('[data-builder-subtitle-shine]');
    if (!subtitleText) return;
    const normalizedMetal = String(metal || '').trim().toLowerCase();
    const metalClass = normalizedMetal === 'white-gold'
      ? 'vf__subtitle--metal-shine-white'
      : normalizedMetal === 'yellow-gold'
        ? 'vf__subtitle--metal-shine-yellow'
        : '';
    if (!metalClass) return;

    subtitleText.classList.remove(
      'vf__subtitle--metal-shine',
      'vf__subtitle--metal-shine-yellow',
      'vf__subtitle--metal-shine-white'
    );
    void subtitleText.offsetWidth;
    subtitleText.classList.add('vf__subtitle--metal-shine', metalClass);
    subtitleText.addEventListener('animationend', () => {
      subtitleText.classList.remove('vf__subtitle--metal-shine', metalClass);
    }, { once: true });
  }

  function updateMobileOverlayLayout() {
    cancelAnimationFrame(mobileOverlayLayoutFrame);
    mobileOverlayLayoutFrame = requestAnimationFrame(() => {
      const isMobile = window.matchMedia('(max-width: 639px)').matches;

      previewOverlays.forEach(overlay => {
        overlay.classList.remove('vf-preview-overlay--expanded');
      });

      if (!isMobile) return;

      const overlaysToExpand = previewOverlays.filter(overlay => {
        if (overlay.hidden || overlay.getBoundingClientRect().width === 0) return false;
        const paintElement = overlay.querySelector('[data-preview-paint]');
        return paintElement && paintElement.scrollWidth > paintElement.clientWidth + 1;
      });

      overlaysToExpand.forEach(overlay => {
        overlay.classList.add('vf-preview-overlay--expanded');
      });
    });
  }

  function updateBuilderPresentation() {
    const make = currentVehicleMake();
    const year = currentVehicleYear();
    const paint = String(selectedPaint?.name || inputPaintName?.value || '').trim();
    const series = String(root.dataset.ringSeriesName || '').trim();
    const paintCode = String(selectedPaint?.code || inputPaintCode?.value || '').trim();
    fitBuilderEyebrow();
    if (builderSubtitle) {
      if (make) {
        const subtitleText = Object.assign(document.createElement('span'), {
          className: 'vf__subtitle-text',
        });
        subtitleText.setAttribute('data-builder-subtitle-text', '');
        const createSubtitleLine = (className, makeAttribute) => {
          const line = Object.assign(document.createElement('span'), { className });
          const makeElement = Object.assign(document.createElement('span'), {
            className: 'vf__subtitle-make',
            textContent: make,
          });
          if (makeAttribute) makeElement.setAttribute('data-builder-subtitle-make', '');
          line.append(document.createTextNode('Build your'), makeElement);
          return line;
        };
        const baseLine = createSubtitleLine('vf__subtitle-line', true);
        const shineLine = createSubtitleLine('vf__subtitle-line vf__subtitle-line--shine', false);
        shineLine.setAttribute('data-builder-subtitle-shine', '');
        shineLine.setAttribute('aria-hidden', 'true');
        subtitleText.append(baseLine, shineLine);
        builderSubtitle.replaceChildren(subtitleText);
        applyBuilderSubtitleOpticalGap(make);
      } else {
        builderSubtitle.textContent = builderSubtitle.dataset.defaultCopy || 'Build your';
      }
      fitBuilderSubtitle();
      if (make && document.fonts?.ready) {
        document.fonts.ready.then(() => {
          applyBuilderSubtitleOpticalGap(make);
          fitBuilderSubtitle();
        });
      }
    }
    previewOverlays.forEach(overlay => {
      overlay.hidden = !make && !series;
      const paintElement = overlay.querySelector('[data-preview-paint]');
      const detailsElement = overlay.querySelector('[data-preview-details]');
      const mobileDetailsElement = overlay.querySelector('[data-preview-details-mobile]');
      if (paintElement) paintElement.textContent = paint ? `${paint}${paintCode ? ` (${paintCode})` : ''}` : 'Neutral Preview';
      if (detailsElement) {
        const details = [
          make ? `<span>Make:</span> <b>${escapeHtml(`${make}${year ? ` ${year}` : ''}`)}</b>` : '',
          series ? `<span>Series:</span> <b>${escapeHtml(series)}</b>` : '',
        ].filter(Boolean);
        detailsElement.innerHTML = details.join('<i aria-hidden="true">|</i>');
      }
      if (mobileDetailsElement) {
        mobileDetailsElement.innerHTML = make
          ? `<span>Make:</span> <b>${escapeHtml(`${make}${year ? ` ${year}` : ''}`)}</b>`
          : '';
      }
    });
    updateMobileOverlayLayout();
  }

  window.addEventListener('resize', updateMobileOverlayLayout, { passive: true, signal: lifecycleSignal });
  window.addEventListener('resize', fitBuilderEyebrow, { passive: true, signal: lifecycleSignal });
  window.addEventListener('resize', fitBuilderSubtitle, { passive: true, signal: lifecycleSignal });

  function buildShareUrl() {
    const program = getSelectedPaintProgram();
    const shareablePaint = selectedPaint?.shareable === true ? selectedPaint : null;
    const nextUrl = window.VeloForgeConfiguratorContract.buildConfigurationUrl(window.location.href, {
      series: root.dataset.ringSeriesHandle || '',
      metal: activeConfiguration?.metalKey || '',
      karat: activeConfiguration?.karatKey || '',
      size: selectedSize,
      make: makeSel?.value || '',
      lockMake: false,
      year: program ? '' : yearSel?.value || '',
      program: program?.value || '',
      paint: shareablePaint ? makeSlug(shareablePaint.name) : '',
      paintCode: shareablePaint?.code || '',
      quantity: qty,
    });
    return nextUrl.toString();
  }

  function announcePreviewAction(message) {
    window.clearTimeout(previewActionStatusTimer);
    previewActionStatuses.forEach(element => {
      element.textContent = message;
      element.hidden = false;
    });
    previewActionStatusTimer = window.setTimeout(() => {
      previewActionStatuses.forEach(element => { element.hidden = true; });
      previewActionStatusTimer = 0;
    }, 3000);
  }

  function shareIdentity() {
    return {
      make: currentVehicleMake(),
      year: currentVehicleYear(),
      paint: String(selectedPaint?.name || inputPaintName?.value || '').trim(),
      paintCode: String(selectedPaint?.code || inputPaintCode?.value || '').trim(),
      series: String(root.dataset.ringSeriesName || '').trim(),
      metal: String(activeConfiguration?.metalKey || root.dataset.metalFamily || '').trim(),
    };
  }

  function sanitizedShareFilename(format) {
    const identity = shareIdentity();
    const parts = ['velo-forge', identity.make, identity.paint, identity.series, format === 'story' ? 'story' : ''];
    return `${parts.map(makeSlug).filter(Boolean).join('-')}.png`;
  }

  function hasShareArtwork(format = 'square') {
    return format === 'story'
      ? root.dataset.shareStoryAvailable === 'true'
      : root.dataset.shareSquareAvailable === 'true';
  }

  function shareAsset(name) {
    return String(root.dataset[name] || '').trim();
  }

  async function loadShareImage(url) {
    if (!url) return null;
    try {
      const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      if (typeof createImageBitmap === 'function') return createImageBitmap(blob);
    } catch (_) {}
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Preview image could not be loaded.'));
      image.src = url;
    });
  }

  function drawCover(context, image, x, y, width, height) {
    if (!image) return;
    const sourceWidth = image.naturalWidth || image.width;
    const sourceHeight = image.naturalHeight || image.height;
    const scale = Math.max(width / sourceWidth, height / sourceHeight);
    const cropWidth = width / scale;
    const cropHeight = height / scale;
    const cropX = (sourceWidth - cropWidth) / 2;
    const cropY = (sourceHeight - cropHeight) / 2;
    context.drawImage(image, cropX, cropY, cropWidth, cropHeight, x, y, width, height);
  }

  function drawContain(context, image, x, y, width, height) {
    if (!image) return;
    const sourceWidth = image.naturalWidth || image.width;
    const sourceHeight = image.naturalHeight || image.height;
    const scale = Math.min(width / sourceWidth, height / sourceHeight);
    const targetWidth = sourceWidth * scale;
    const targetHeight = sourceHeight * scale;
    context.drawImage(image, x + (width - targetWidth) / 2, y + (height - targetHeight) / 2, targetWidth, targetHeight);
  }

  function maskedPaintLayer(inlay, hex, size) {
    const layer = document.createElement('canvas');
    layer.width = size;
    layer.height = size;
    const context = layer.getContext('2d');
    drawCover(context, inlay, 0, 0, size, size);
    context.globalCompositeOperation = 'source-in';
    context.fillStyle = hex;
    context.fillRect(0, 0, size, size);
    return layer;
  }

  function fittedBackgroundLayer(background, width, height) {
    const layer = document.createElement('canvas');
    layer.width = width;
    layer.height = height;
    const context = layer.getContext('2d');
    drawCover(context, background, 0, 0, width, height);
    return layer;
  }

  function backgroundWithoutInlay(fittedBackground, inlay, width, height, ringY, ringSize) {
    const layer = document.createElement('canvas');
    layer.width = width;
    layer.height = height;
    const context = layer.getContext('2d');
    context.drawImage(fittedBackground, 0, 0);
    context.globalCompositeOperation = 'destination-out';
    drawCover(context, inlay, 0, ringY, ringSize, ringSize);
    context.globalCompositeOperation = 'source-over';
    return layer;
  }

  function ringMetalFromBackground(fittedBackground, foreground, inlay, width, height, ringY, ringSize) {
    const silhouette = document.createElement('canvas');
    silhouette.width = width;
    silhouette.height = height;
    const silhouetteContext = silhouette.getContext('2d');
    drawCover(silhouetteContext, foreground, 0, ringY, ringSize, ringSize);
    silhouetteContext.globalCompositeOperation = 'source-in';
    silhouetteContext.fillStyle = '#fff';
    silhouetteContext.fillRect(0, 0, width, height);
    silhouetteContext.globalCompositeOperation = 'destination-out';
    drawCover(silhouetteContext, inlay, 0, ringY, ringSize, ringSize);
    silhouetteContext.globalCompositeOperation = 'source-over';

    const metal = document.createElement('canvas');
    metal.width = width;
    metal.height = height;
    const metalContext = metal.getContext('2d');
    metalContext.drawImage(fittedBackground, 0, 0);
    metalContext.globalCompositeOperation = 'destination-in';
    metalContext.drawImage(silhouette, 0, 0);
    metalContext.globalCompositeOperation = 'source-over';
    return metal;
  }

  async function renderConfiguredRing(size = 1080, { transparentForeground = false } = {}) {
    const preview = root.querySelector('[data-ring-preview]');
    const inlayElement = preview?.querySelector('.vf-ring-preview__inlay');
    const baseElement = preview?.querySelector('[data-ring-base-img]');
    const overlayElement = preview?.querySelector('.vf-ring-preview__lighting--overlay');
    const shadowElement = preview?.querySelector('.vf-ring-preview__lighting--shadow');
    const identity = shareIdentity();
    const foregroundUrl = identity.metal === 'white-gold' ? shareAsset('shareForegroundWhite') : shareAsset('shareForegroundYellow');
    const [inlay, base, lighting, shadow, foreground] = await Promise.all([
      loadShareImage(inlayElement?.currentSrc || inlayElement?.src),
      loadShareImage(baseElement?.currentSrc || baseElement?.src),
      loadShareImage(overlayElement?.currentSrc || overlayElement?.src),
      loadShareImage(shadowElement?.currentSrc || shadowElement?.src),
      transparentForeground ? loadShareImage(foregroundUrl) : null,
    ]);
    if (!inlay || !base) throw new Error('Configured preview layers are unavailable.');
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d');
    const hex = String(selectedPaint?.hex || inputPaintHex?.value || '').trim();
    const customAssets = preview?.classList.contains('vf-ring-preview--custom-assets');
    drawCover(context, inlay, 0, 0, size, size);
    if (customAssets) {
      drawCover(context, transparentForeground && foreground ? foreground : base, 0, 0, size, size);
      if (hex) {
        context.globalCompositeOperation = 'multiply';
        context.drawImage(maskedPaintLayer(inlay, hex, size), 0, 0);
        context.globalCompositeOperation = 'source-over';
      }
    } else {
      if (hex) {
        context.drawImage(maskedPaintLayer(inlay, hex, size), 0, 0);
        if (lighting) {
          context.globalCompositeOperation = 'overlay';
          drawCover(context, lighting, 0, 0, size, size);
        }
        if (shadow) {
          context.globalCompositeOperation = 'darken';
          drawCover(context, shadow, 0, 0, size, size);
        }
        context.globalCompositeOperation = 'source-over';
      }
      drawCover(context, base, 0, 0, size, size);
    }
    [inlay, base, lighting, shadow, foreground].forEach(image => image?.close?.());
    return canvas;
  }

  function pdfConfigurationSnapshot() {
    const notes = savedNotesData || (savedNotes ? parseNotesText(savedNotes) : null);
    const make = String(notes?.make || currentVehicleMake() || '').trim();
    const year = String(notes?.year || currentVehicleYear() || '').trim();
    const paintName = String(notes?.paintName || selectedPaint?.name || inputPaintName?.value || '').trim();
    const paintCode = String(notes?.paintCode || selectedPaint?.code || inputPaintCode?.value || '').trim();
    const paintProgram = getSelectedPaintProgram(makeSel?.value || make, yearSel?.value || '');
    const isPaintToSample = Boolean(
      paintProgram ||
      /^pts$/i.test(paintCode) ||
      /paint\s+to\s+sample/i.test(`${paintName} ${notes?.raw || ''}`)
    );
    const resolvedVariant = resolveConfigurationVariant(activeConfiguration, selectedSize);
    const price = resolvedVariant?.eventVariant?.price || null;
    const missing = [];
    if (!String(root.dataset.ringSeriesName || '').trim()) missing.push('Ring Series');
    if (!isConfigurationSelectionComplete() || !activeConfiguration?.metalLabel) missing.push('Metal');
    if (!isConfigurationSelectionComplete() || !activeConfiguration?.karatLabel) missing.push('Karat');
    if (!make) missing.push('Vehicle Make');
    if (!paintName && !paintCode && !isPaintToSample) missing.push('Paint selection');
    if (!selectedSize || !resolvedVariant) missing.push('Ring Size');

    return {
      missing,
      series: String(root.dataset.ringSeriesName || '').trim(),
      metal: String(activeConfiguration?.metalLabel || '').trim(),
      metalKey: String(activeConfiguration?.metalKey || '').trim(),
      karat: String(activeConfiguration?.karatLabel || '').trim(),
      make,
      year,
      paintName: paintName || (isPaintToSample ? 'Paint to Sample' : ''),
      paintCode: isPaintToSample ? 'PTS' : paintCode,
      isPaintToSample,
      size: String(selectedSize || '').trim(),
      variantId: String(resolvedVariant?.id || ''),
      priceAmount: String(price?.amount || ''),
      currencyCode: String(price?.currencyCode || '').trim(),
    };
  }

  function drawFittedText(context, value, x, y, maxWidth, initialSize, weight = 700, family = '"Cabinet Grotesk", Arial, sans-serif', letterSpacing = '0px') {
    let size = initialSize;
    do {
      context.font = `${weight} ${size}px ${family}`;
      context.letterSpacing = letterSpacing;
      if (context.measureText(value).width <= maxWidth) break;
      size -= 2;
    } while (size > 28);
    context.fillText(value, x, y);
  }

  const SHARE_ARTWORK_TYPOGRAPHY = Object.freeze({
    square: Object.freeze({
      primaryY: 930,
      primarySize: 31,
      primaryWeight: 700,
      primaryFamily: 'Manrope, sans-serif',
      primaryTracking: '0.28em',
      secondaryY: 976,
      secondarySize: 19,
      secondaryWeight: 400,
      secondaryFamily: 'Manrope, sans-serif',
      secondaryTracking: '0.1em',
    }),
    story: Object.freeze({
      primaryY: 1660,
      primarySize: 38,
      primaryWeight: 700,
      primaryFamily: 'Manrope, sans-serif',
      primaryTracking: '0.28em',
      secondaryY: 1715,
      secondarySize: 23,
      secondaryWeight: 400,
      secondaryFamily: 'Manrope, sans-serif',
      secondaryTracking: '0.1em',
    }),
  });

  function shareArtworkCopy(identity) {
    return Object.freeze({
      primary: identity.paint
        ? `${identity.paint}${identity.paintCode ? ` (${identity.paintCode})` : ''}`.toUpperCase()
        : 'NEUTRAL PREVIEW',
      secondary: [
        identity.make ? `MAKE: ${identity.make}` : '',
        identity.series ? `SERIES: ${identity.series}` : '',
      ].filter(Boolean).join('  |  ').toUpperCase(),
    });
  }

  function drawShareArtworkText(context, format, identity, width) {
    const typography = SHARE_ARTWORK_TYPOGRAPHY[format];
    const copy = shareArtworkCopy(identity);
    context.textAlign = 'center';
    context.textBaseline = 'alphabetic';
    context.fillStyle = 'rgba(255,255,255,0.8)';
    drawFittedText(context, copy.primary, width / 2, typography.primaryY, width - 180, typography.primarySize, typography.primaryWeight, typography.primaryFamily, typography.primaryTracking);
    if (!copy.secondary) return;
    context.fillStyle = 'rgba(255,255,255,0.5)';
    drawFittedText(context, copy.secondary, width / 2, typography.secondaryY, width - 180, typography.secondarySize, typography.secondaryWeight, typography.secondaryFamily, typography.secondaryTracking);
  }

  function signatureStoryPaintLayer(channelMaskSource, hex) {
    const canvas = document.createElement('canvas');
    canvas.width = channelMaskSource.naturalWidth || channelMaskSource.width;
    canvas.height = channelMaskSource.naturalHeight || channelMaskSource.height;
    const sampleCanvas = document.createElement('canvas');
    sampleCanvas.width = canvas.width;
    sampleCanvas.height = canvas.height;
    const sampleContext = sampleCanvas.getContext('2d', { willReadFrequently: true });
    sampleContext.drawImage(channelMaskSource, 0, 0);
    const channelMask = sampleContext.getImageData(0, 0, canvas.width, canvas.height).data;
    const context = canvas.getContext('2d');
    const imageData = context.createImageData(canvas.width, canvas.height);
    const data = imageData.data;
    const normalizedHex = String(hex || '').trim().replace(/^#/, '');
    const expandedHex = normalizedHex.length === 3
      ? normalizedHex.split('').map(character => character + character).join('')
      : normalizedHex;
    if (!/^[0-9a-f]{6}$/i.test(expandedHex)) return canvas;
    const color = {
      r: Number.parseInt(expandedHex.slice(0, 2), 16),
      g: Number.parseInt(expandedHex.slice(2, 4), 16),
      b: Number.parseInt(expandedHex.slice(4, 6), 16),
    };
    for (let index = 0; index < data.length; index += 4) {
      data[index] = color.r;
      data[index + 1] = color.g;
      data[index + 2] = color.b;
      data[index + 3] = channelMask[index + 3];
    }
    context.putImageData(imageData, 0, 0);
    return canvas;
  }

  function signatureStoryAssetUrls(metal) {
    const prefix = metal === 'white-gold' ? 'shareStoryWg' : 'shareStoryYg';
    return [1, 2, 3, 4].map(number => root.dataset[`${prefix}${number}`]).filter(Boolean);
  }

  async function generateShareArtwork(format) {
    if (!hasShareArtwork(format)) throw new Error(`Share artwork is not available for ${root.dataset.ringSeriesName || 'this Series'}.`);
    const story = format === 'story';
    const artworkFormat = story ? 'story' : 'square';
    const width = 1080;
    const height = story ? 1920 : 1080;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    await document.fonts?.ready;
    await document.fonts?.load('700 38px Manrope');
    await document.fonts?.load('400 23px Manrope');
    const identity = shareIdentity();
    const preview = root.querySelector('[data-ring-preview]');
    const inlayElement = preview?.querySelector('.vf-ring-preview__inlay');
    const lightingElement = preview?.querySelector('.vf-ring-preview__lighting--overlay');
    const shadowElement = preview?.querySelector('.vf-ring-preview__lighting--shadow');
    const logoUrl = shareAsset('shareLogoOverlay');
    const foregroundUrl = identity.metal === 'white-gold' ? shareAsset('shareForegroundWhite') : shareAsset('shareForegroundYellow');
    const storyUrl = identity.metal === 'white-gold' ? shareAsset('shareStoryWhite') : shareAsset('shareStoryYellow');
    const squareUrl = identity.metal === 'white-gold' ? shareAsset('shareSquareWhite') : shareAsset('shareSquareYellow');
    const backgroundUrl = story ? storyUrl : squareUrl;
    const signatureStoryUrls = story ? signatureStoryAssetUrls(identity.metal) : [];
    const usesSignatureStoryStack = story && signatureStoryUrls.length === 4;
    const seriesHandle = String(root.dataset.ringSeriesHandle || '').trim();
    const usesPaddockSquareStack = !story && (seriesHandle === 'paddock' || seriesHandle === 'paddock-series');
    const hex = String(selectedPaint?.hex || inputPaintHex?.value || '').trim();
    if (usesSignatureStoryStack) {
      const [layer1, layer2, layer3, layer4] = await Promise.all(signatureStoryUrls.map(loadShareImage));
      const storyLayers = [layer1, layer2, layer3, layer4];
      if (storyLayers.some(layer => !layer)) throw new Error('Required Signature Story artwork layers are unavailable.');
      if (!storyLayers.every(layer => (layer.naturalWidth || layer.width) === width && (layer.naturalHeight || layer.height) === height)) {
        storyLayers.forEach(layer => layer?.close?.());
        throw new Error('Signature Story artwork layers do not share the 1080 x 1920 registration.');
      }
      context.globalCompositeOperation = 'source-over';
      context.drawImage(layer1, 0, 0, width, height, 0, 0, width, height);
      if (hex) context.drawImage(signatureStoryPaintLayer(layer1, hex), 0, 0, width, height, 0, 0, width, height);
      context.drawImage(layer2, 0, 0, width, height, 0, 0, width, height);
      context.drawImage(layer3, 0, 0, width, height, 0, 0, width, height);
      context.drawImage(layer4, 0, 0, width, height, 0, 0, width, height);
      storyLayers.forEach(layer => layer.close?.());
    } else if (usesPaddockSquareStack) {
      const [background, logo, topRing, inlay] = await Promise.all([
        loadShareImage(squareUrl),
        loadShareImage(logoUrl),
        loadShareImage(foregroundUrl),
        loadShareImage(inlayElement?.currentSrc || inlayElement?.src),
      ]);
      if (!background || !logo || !topRing || !inlay) throw new Error('Required Paddock Square artwork layers are unavailable.');
      drawCover(context, background, 0, 0, width, height);
      context.save();
      context.globalAlpha = 0.58;
      drawContain(context, logo, 0, 0, width, height);
      context.restore();
      drawCover(context, topRing, 0, 0, width, height);
      drawCover(context, inlay, 0, 0, width, height);
      if (hex) {
        context.globalCompositeOperation = 'multiply';
        context.drawImage(maskedPaintLayer(inlay, hex, width), 0, 0);
        context.globalCompositeOperation = 'source-over';
      }
      [background, logo, topRing, inlay].forEach(image => image?.close?.());
    } else {
      const [background, logo, inlay, foreground, lighting, shadow] = await Promise.all([
        loadShareImage(backgroundUrl),
        loadShareImage(logoUrl),
        loadShareImage(inlayElement?.currentSrc || inlayElement?.src),
        loadShareImage(foregroundUrl),
        loadShareImage(lightingElement?.currentSrc || lightingElement?.src),
        loadShareImage(shadowElement?.currentSrc || shadowElement?.src),
      ]);
      if (!background || !logo || !inlay || !foreground) throw new Error('Required share artwork layers are unavailable.');
      const ringSize = 1080;
      const ringY = story ? 420 : 0;
      const fittedBackground = fittedBackgroundLayer(background, width, height);
      context.fillStyle = '#080808';
      context.fillRect(0, 0, width, height);
      if (hex) {
        context.drawImage(maskedPaintLayer(inlay, hex, ringSize), 0, ringY, ringSize, ringSize);
        if (lighting) {
          context.globalCompositeOperation = 'overlay';
          drawCover(context, lighting, 0, ringY, ringSize, ringSize);
        }
        if (shadow) {
          context.globalCompositeOperation = 'darken';
          drawCover(context, shadow, 0, ringY, ringSize, ringSize);
        }
        context.globalCompositeOperation = 'source-over';
      }
      if (story) {
        context.drawImage(backgroundWithoutInlay(fittedBackground, inlay, width, height, ringY, ringSize), 0, 0);
        context.save();
        context.globalAlpha = 0.58;
        drawContain(context, logo, 0, ringY, ringSize, ringSize);
        context.restore();
        context.drawImage(ringMetalFromBackground(fittedBackground, foreground, inlay, width, height, ringY, ringSize), 0, 0);
      } else {
        context.drawImage(fittedBackground, 0, 0);
        context.save();
        context.globalAlpha = 0.58;
        drawContain(context, logo, 0, ringY, ringSize, ringSize);
        context.restore();
        context.drawImage(foreground, 0, ringY, ringSize, ringSize);
      }
      [background, logo, inlay, foreground, lighting, shadow].forEach(image => image?.close?.());
    }

    drawShareArtworkText(context, artworkFormat, identity, width);
    return new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Image generation failed.')), 'image/png'));
  }

  function downloadShareArtwork(blob, filename) {
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function shouldUseNativeStoryShare() {
    return Boolean(
      typeof navigator.share === 'function' &&
      typeof navigator.canShare === 'function' &&
      window.matchMedia?.('(pointer: coarse)').matches &&
      window.innerWidth <= 1024
    );
  }

  async function createInstagramStory(button) {
    button.disabled = true;
    try {
      if (!hasShareArtwork('story')) {
        announcePreviewAction(`${root.dataset.ringSeriesName || 'This Series'} Instagram artwork is coming soon.`);
        return;
      }
      const blob = await generateShareArtwork('story');
      const filename = sanitizedShareFilename('story');
      const file = typeof File === 'function' ? new File([blob], filename, { type: 'image/png' }) : null;
      if (file && shouldUseNativeStoryShare() && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file] });
          announcePreviewAction('Instagram Story image ready to share.');
          return;
        } catch (_) {}
      }
      downloadShareArtwork(blob, filename);
      announcePreviewAction('Instagram Story image downloaded.');
    } catch (error) {
      if (error?.name !== 'AbortError') announcePreviewAction('Unable to create the Instagram Story image. Please try again.');
    } finally {
      button.disabled = false;
    }
  }

  function shareOnFacebook() {
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(buildShareUrl())}`;
    const popup = window.open(shareUrl, 'vf-facebook-share', 'width=720,height=680');
    if (popup) {
      try { popup.opener = null; } catch (_) {}
      announcePreviewAction('Facebook sharing opened in a new window.');
    } else {
      announcePreviewAction('Facebook sharing was blocked. Allow pop-ups and try again.');
    }
  }

  async function downloadSquareArtwork(button) {
    button.disabled = true;
    try {
      if (!hasShareArtwork('square')) {
        announcePreviewAction(`${root.dataset.ringSeriesName || 'This Series'} download artwork is coming soon.`);
        return;
      }
      const blob = await generateShareArtwork('square');
      downloadShareArtwork(blob, sanitizedShareFilename('square'));
      announcePreviewAction('Configuration image downloaded.');
    } catch (_) {
      announcePreviewAction('Unable to create the configuration image. Please try again.');
    } finally {
      button.disabled = false;
    }
  }

  async function shareConfiguration() {
    const url = buildShareUrl();
    try {
      if (typeof navigator.share === 'function' && navigator.maxTouchPoints > 0) {
        try {
          await navigator.share({ title: 'Velo Forge ring configuration', url });
          announcePreviewAction('Configuration shared.');
          return;
        } catch (error) {
          if (error?.name === 'AbortError') return;
        }
      }
      await navigator.clipboard.writeText(url);
      announcePreviewAction('Configuration link copied.');
    } catch (error) {
      if (error?.name !== 'AbortError') announcePreviewAction('Unable to share this configuration. Please copy the page URL.');
    }
  }

  previewInstagramButtons.forEach(button => button.addEventListener('click', () => createInstagramStory(button), { signal: lifecycleSignal }));
  previewFacebookButtons.forEach(button => button.addEventListener('click', shareOnFacebook, { signal: lifecycleSignal }));
  previewDownloadButtons.forEach(button => button.addEventListener('click', () => downloadSquareArtwork(button), { signal: lifecycleSignal }));
  previewShareButtons.forEach(button => button.addEventListener('click', shareConfiguration, { signal: lifecycleSignal }));

  async function applyConfigurationUrl(urlState = configurationUrlState()) {
    if (!urlState.present || !requestCoordinator.isActive()) return false;
    applyingUrlState = true;
    try {
      const seriesHandle = root.dataset.ringSeriesHandle || '';
      if (urlState.series && urlState.series !== seriesHandle) {
        warnInDevelopmentOnce('url-series', `Ignored unsupported ring series URL value "${urlState.series}".`);
      }

      const metalValid = ringConfigurations.some(configuration => configuration.metalKey === urlState.metal);
      const karatValid = ringConfigurations.some(configuration => configuration.karatKey === urlState.karat);
      if (metalValid) intentionalMetalSelected = true;
      if (karatValid) intentionalKaratSelected = true;
      if (metalValid || karatValid) {
        const metalKey = metalValid ? urlState.metal : activeConfiguration?.metalKey;
        const karatKey = karatValid ? urlState.karat : activeConfiguration?.karatKey;
        const configuration = ringConfigurations.find(item => item.product && item.metalKey === metalKey && item.karatKey === karatKey);
        if (configuration) switchRingConfiguration(configuration);
        syncConfigurationSelectionState();
      }

      if (!lockMake && urlState.make) {
        const resolvedMake = paintRegistry.makes.find(make => makeSlug(make) === urlState.make);
        if (resolvedMake && makeSel.querySelector(`option[value="${CSSescape(resolvedMake)}"]`)) {
          makeSel.value = resolvedMake;
          await onMakeChange();
        }
      }

      const requestedSecondary = urlState.program || urlState.year;
      if (requestedSecondary && yearSel.querySelector(`option[value="${CSSescape(requestedSecondary)}"]`)) {
        yearSel.value = requestedSecondary;
        await onYearChange();
      }

      if ((urlState.paint || urlState.paintCode) && paintGrid) {
        const matches = Array.from(paintGrid.querySelectorAll('.vf-paint-swatch')).filter(button => {
          const nameMatches = !urlState.paint || makeSlug(button.dataset.name || '') === urlState.paint;
          const codeMatches = !urlState.paintCode || (button.dataset.code || '').toLowerCase() === urlState.paintCode.toLowerCase();
          return nameMatches && codeMatches;
        });
        if (matches.length === 1) selectSwatch(matches[0]);
        else {
          const catalogMatches = makePaintSearchCatalog.filter(record => {
            const nameMatches = !urlState.paint || makeSlug(record.name) === urlState.paint;
            const codeMatches = !urlState.paintCode || record.code.toLowerCase() === urlState.paintCode.toLowerCase();
            return nameMatches && codeMatches;
          });
          if (catalogMatches.length === 1) selectPaintSearchResult(catalogMatches[0]);
        }
      }

      if (urlState.size) {
        const sizeButton = sizeGrid?.querySelector(`[data-size-value="${CSSescape(urlState.size)}"]`);
        if (sizeButton && !sizeButton.disabled && sizeButton.getAttribute('aria-disabled') !== 'true') {
          selectSizeButton(sizeButton, { emitEvent: false });
        }
      }

      if (urlState.quantity) {
        qty = urlState.quantity;
        updateQty();
      }
      saveState();
      return true;
    } finally {
      applyingUrlState = false;
    }
  }

  function findProgramBySourceMake(sourceMake) {
    for (const programs of Object.values(paintRegistry.specialPaintPrograms || {})) {
      const match = (programs || []).find(program => program.sourceMake === sourceMake);
      if (match) return match;
    }
    return null;
  }

  function getCombobox(type) {
    return type === 'make'
      ? { select: makeSel, input: makeComboInput, list: makeComboList }
      : { select: yearSel, input: yearComboInput, list: yearComboList };
  }

  function updateSelectAppearance(select) {
    if (!select) return;
    const input = select === makeSel ? makeComboInput : yearComboInput;
    select.classList.toggle('vf-select--has-value', Boolean(select.value));
    input?.classList.toggle('vf-select--has-value', Boolean(select.value));
  }

  function syncComboboxValue(type) {
    const { select, input } = getCombobox(type);
    if (!select || !input) return;
    const selectedOption = select.options[select.selectedIndex];
    input.value = select.value && selectedOption ? selectedOption.textContent : '';
    updateSelectAppearance(select);
    if (type === 'make') updateMakePresentation();
  }

  function updateMakePresentation() {
    if (!makeSel || !makeCombobox || !makeSummary || lockMake) return;
    const selectedOption = makeSel.options[makeSel.selectedIndex];
    const displayMake = makeSel.value && selectedOption ? selectedOption.textContent.trim() : '';
    const showSummary = Boolean(displayMake) && !editingMake;
    makeSummary.hidden = !showSummary;
    makeCombobox.hidden = showSummary;
    if (makeLabel) makeLabel.hidden = showSummary;
    if (makeEditButton) {
      makeEditButton.textContent = displayMake;
      makeEditButton.setAttribute('aria-label', displayMake ? `Change vehicle make, currently ${displayMake}` : 'Change vehicle make');
    }
  }

  function editVehicleMake() {
    if (!makeComboInput || lockMake) return;
    editingMake = true;
    updateMakePresentation();
    requestAnimationFrame(() => makeComboInput.focus());
  }

  function closeCombobox(type) {
    const { input, list } = getCombobox(type);
    if (list) list.hidden = true;
    if (input) {
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    }
  }

  function renderComboboxOptions(type, query = '') {
    const { select, input, list } = getCombobox(type);
    if (!select || !input || !list) return [];
    const normalizedQuery = query.trim().toLowerCase();
    const options = Array.from(select.options).filter(option =>
      option.value && (!normalizedQuery || option.textContent.toLowerCase().includes(normalizedQuery)));

    list.innerHTML = '';
    options.forEach((option, index) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.id = `${optionIdPrefix}-${type}-option-${index}`;
      item.className = 'vf-combobox__option';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', String(option.value === select.value));
      item.dataset.value = option.value;
      item.textContent = option.textContent;
      item.addEventListener('mousedown', event => event.preventDefault(), { signal: lifecycleSignal });
      item.addEventListener('click', () => selectComboboxOption(type, option.value), { signal: lifecycleSignal });
      list.appendChild(item);
    });

    if (options.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'vf-combobox__empty';
      empty.textContent = 'No matches found';
      list.appendChild(empty);
    }
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    return options;
  }

  function selectComboboxOption(type, value) {
    const { select } = getCombobox(type);
    if (!select) return;
    select.value = value;
    syncComboboxValue(type);
    closeCombobox(type);
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function setupCombobox(type) {
    const { select, input, list } = getCombobox(type);
    if (!select || !input || !list) return;
    let activeIndex = -1;

    const setActiveOption = nextIndex => {
      const items = Array.from(list.querySelectorAll('.vf-combobox__option'));
      if (!items.length) return;
      activeIndex = Math.max(0, Math.min(nextIndex, items.length - 1));
      items.forEach((item, index) => {
        const active = index === activeIndex;
        item.classList.toggle('vf-combobox__option--active', active);
        item.setAttribute('aria-selected', String(active));
      });
      input.setAttribute('aria-activedescendant', items[activeIndex].id);
      items[activeIndex].scrollIntoView({ block: 'nearest' });
    };

    input.addEventListener('focus', () => {
      activeIndex = -1;
      renderComboboxOptions(type, input.value === select.options[select.selectedIndex]?.textContent ? '' : input.value);
      if (select.value) input.select();
    }, { signal: lifecycleSignal });
    input.addEventListener('click', () => {
      activeIndex = -1;
      renderComboboxOptions(type, input.value === select.options[select.selectedIndex]?.textContent ? '' : input.value);
      if (select.value) input.select();
    }, { signal: lifecycleSignal });
    input.addEventListener('input', () => {
      const query = input.value;
      if (select.value) {
        select.value = '';
        updateSelectAppearance(select);
        select.dispatchEvent(new Event('change', { bubbles: true }));
        input.value = query;
      }
      activeIndex = -1;
      renderComboboxOptions(type, query);
    }, { signal: lifecycleSignal });
    input.addEventListener('keydown', event => {
      const items = Array.from(list.querySelectorAll('.vf-combobox__option'));
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (list.hidden) renderComboboxOptions(type, input.value);
        setActiveOption(activeIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveOption(activeIndex <= 0 ? items.length - 1 : activeIndex - 1);
      } else if (event.key === 'Enter') {
        if (list.hidden) return;
        event.preventDefault();
        const exact = items.find(item => item.textContent.trim().toLowerCase() === input.value.trim().toLowerCase());
        const chosen = exact || items[Math.max(activeIndex, 0)];
        if (chosen) selectComboboxOption(type, chosen.dataset.value);
      } else if (event.key === 'Escape') {
        closeCombobox(type);
        if (type === 'make') editingMake = false;
        syncComboboxValue(type);
      }
    }, { signal: lifecycleSignal });
    input.addEventListener('blur', () => {
      const timer = window.setTimeout(() => {
        rootTimers.delete(timer);
        if (destroyed || !root.isConnected) return;
        closeCombobox(type);
        if (type === 'make') editingMake = false;
        syncComboboxValue(type);
      }, 100);
      rootTimers.add(timer);
    }, { signal: lifecycleSignal });
  }

  function configureSecondaryField() {
    if (secondaryLabel) secondaryLabel.textContent = 'Vehicle Year';
    if (vehicleHelper) vehicleHelper.textContent = 'Select your year and paint to preview your ring finish.';
    if (yearSel) yearSel.innerHTML = '<option value="">Select year</option>';
    if (yearComboInput) yearComboInput.placeholder = 'Select year';
    syncComboboxValue('year');
  }

  function getPaintDataUrl(source) {
    const indexUrl = root.dataset.paintIndexUrl || '';
    const slug = typeof source === 'string'
      ? makeSlug(source)
      : source?.sourceSlug || makeSlug(source?.sourceMake || '');
    return window.VeloForgeConfiguratorContract.resolvePaintDataUrl({ assetMap: paintAssetMap, slug, indexUrl });
  }

  function getAssetUrl(filename) {
    const indexUrl = root.dataset.paintIndexUrl || '';
    return window.VeloForgeConfiguratorContract.resolveThemeAssetUrl({ assetMap: paintAssetMap, filename, indexUrl });
  }

  function warnInDevelopmentOnce(key, message) {
    const isDevelopment = Boolean(window.Shopify?.designMode) ||
      ['localhost', '127.0.0.1'].includes(window.location.hostname);
    if (!isDevelopment || developmentWarnings.has(key)) return;
    developmentWarnings.add(key);
    console.warn(`VeloForge: ${message}`);
  }

  async function loadDisplayNameLookup(program) {
    const filename = program?.displayNameLookup;
    if (!filename) return {};
    return displayNameLookupCache.get(filename, async () => {
      const res = await fetch(getAssetUrl(filename));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      return json;
    }, json => Boolean(json && !Array.isArray(json) && typeof json === 'object'));
  }

  async function loadPaintData(source) {
    const sourceMake = typeof source === 'string' ? source : source?.sourceMake;
    const sourceSlug = typeof source === 'string' ? makeSlug(source) : source?.sourceSlug;
    if (!sourceMake || !sourceSlug) throw new Error('Paint source is incomplete');
    return makeCache.get(sourceSlug, async () => {
      const res = await fetch(getPaintDataUrl({ sourceMake, sourceSlug }));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      return json;
    }, json => Boolean(json && !Array.isArray(json) && typeof json === 'object' && Object.keys(json).length));
  }

  function loadMakeData(make) {
    return loadPaintData({ sourceMake: make, sourceSlug: makeSlug(make) });
  }

  function getYearsForMake(makeData, make) {
    const years = new Set();
    const invalidYears = new Set();
    const validator = window.VeloForgeYearValidation;
    Object.values(makeData || {}).forEach((yearMap) => {
      Object.keys(yearMap || {}).forEach((year) => {
        if (validator?.isValidVehicleYear(year)) years.add(year);
        else invalidYears.add(year);
      });
    });

    if (invalidYears.size) {
      warnInDevelopmentOnce(
        `invalid-years:${make}:${Array.from(invalidYears).sort().join(',')}`,
        `Filtered invalid vehicle year values for ${make}: ${Array.from(invalidYears).sort().join(', ')}.`
      );
    }

    return Array.from(years).sort((left, right) => Number(right) - Number(left) || String(right).localeCompare(String(left)));
  }

  function getPaintsForMakeYear(makeData, year) {
    const merged = [];
    const seen = new Set();

    Object.values(makeData || {}).forEach((yearMap) => {
      const paints = yearMap?.[year];
      if (!Array.isArray(paints)) return;

      paints.forEach((paint) => {
        const key = [paint.display_name, paint.paint_code, paint.hex_value].join('|');
        if (seen.has(key)) return;
        seen.add(key);
        merged.push(paint);
      });
    });

    return merged;
  }

  function getPaintsForProgram(programData, program) {
    if (program.selectionMode !== 'merge-models') return [];

    const merged = [];
    const seen = new Set();
    for (const collections of Object.values(programData || {})) {
      const paints = collections?.[program.collectionKey];
      if (!Array.isArray(paints)) continue;

      for (const paint of paints) {
        const preferredValue = program.deduplicateBy ? paint[program.deduplicateBy] : null;
        const key = preferredValue != null && preferredValue !== ''
          ? `${program.deduplicateBy}:${preferredValue}`
          : [paint.display_name, paint.paint_code, paint.hex_value].join('|');
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(paint);
      }
    }
    return merged;
  }

  function applyReviewedDisplayNames(paints, program, lookup) {
    if (!program.displayNameLookup) return paints;

    return paints.map(paint => {
      const sourceDisplayName = paint.display_name;
      const sourceId = paint.porsche_asset_id;
      const entry = sourceId != null ? lookup[String(sourceId)] : null;
      const valid = Boolean(
        sourceId != null &&
        entry &&
        entry.sourceDisplayName === sourceDisplayName &&
        entry.paintCode === paint.paint_code &&
        typeof entry.customerDisplayName === 'string' &&
        entry.customerDisplayName.trim()
      );

      if (!valid) {
        const warningId = sourceId != null ? sourceId : `${sourceDisplayName}:${paint.paint_code}`;
        warnInDevelopmentOnce(
          `lookup-entry:${program.id}`,
          `Invalid display-name lookup entry for ${program.id} paint ${warningId}; using the source name.`
        );
      }

      return {
        ...paint,
        sourceDisplayName,
        customerDisplayName: valid ? entry.customerDisplayName.trim() : sourceDisplayName,
      };
    });
  }

  function paintSearchIdentity(paint) {
    return [
      paint.customerDisplayName || paint.display_name || '',
      paint.paint_code || paint.code || '',
      paint.hex_value || paint.hex || '',
    ].map(value => String(value).trim().toLowerCase()).join('|');
  }

  function addPaintSearchRecord(records, paint, reference = {}) {
    const name = String(paint.customerDisplayName || paint.display_name || '').trim();
    const code = String(paint.paint_code || '').trim();
    const hex = String(paint.hex_value || '').trim();
    if (!name || !hex) return;
    const key = paintSearchIdentity(paint);
    let record = records.get(key);
    if (!record) {
      record = {
        stableId: String(paint.porsche_asset_id ?? paint.slug ?? key),
        name,
        customerDisplayName: name,
        sourceDisplayName: String(paint.sourceDisplayName || paint.display_name || name).trim(),
        code,
        hex,
        shareable: true,
        years: new Set(),
        programs: new Set(),
        programValues: new Set(),
      };
      records.set(key, record);
    }
    if (reference.year) record.years.add(String(reference.year));
    if (reference.programLabel) record.programs.add(String(reference.programLabel));
    if (reference.programValue) record.programValues.add(String(reference.programValue));
  }

  async function buildMakePaintSearchCatalog(make, makeData) {
    const records = new Map();
    Object.values(makeData || {}).forEach(yearMap => {
      Object.entries(yearMap || {}).forEach(([year, paints]) => {
        if (!Array.isArray(paints)) return;
        paints.forEach(paint => addPaintSearchRecord(records, paint, { year }));
      });
    });

    for (const program of getSpecialPaintPrograms(make)) {
      try {
        const [programData, lookup] = await Promise.all([
          loadPaintData(program),
          program.displayNameLookup ? loadDisplayNameLookup(program).catch(() => ({})) : Promise.resolve({}),
        ]);
        const paints = applyReviewedDisplayNames(getPaintsForProgram(programData, program), program, lookup);
        paints.forEach(paint => addPaintSearchRecord(records, paint, {
          programLabel: program.label,
          programValue: program.value,
        }));
      } catch (_) {
        warnInDevelopmentOnce(`paint-search-program:${program.id}`, `Could not include ${program.label} in paint-name search; the normal program selector remains available.`);
      }
    }

    return Array.from(records.values()).map(record => ({
      ...record,
      years: Array.from(record.years).sort((a, b) => Number(b) - Number(a)),
      programs: Array.from(record.programs),
      programValues: Array.from(record.programValues),
    })).sort((left, right) => left.name.localeCompare(right.name) || left.code.localeCompare(right.code));
  }

  function paintSearchReference(record) {
    const references = [...record.programs];
    if (record.years.length === 1) references.push(record.years[0]);
    else if (record.years.length > 1) references.push(`${record.years.at(-1)}–${record.years[0]}`);
    return references.join(' · ');
  }

  function findSavedPaintInSearchCatalog(identity) {
    return window.VeloForgeConfiguratorContract.findSavedPaint(makePaintSearchCatalog.map(record => ({
      record,
      stableId: record.stableId,
      customerDisplayName: record.name,
      sourceDisplayName: record.sourceDisplayName,
      code: record.code,
      hex: record.hex,
    })), identity)?.record || null;
  }

  function closePaintSearch() {
    if (paintSearchList) paintSearchList.hidden = true;
    if (inputPaintName) {
      inputPaintName.setAttribute('aria-expanded', 'false');
      inputPaintName.removeAttribute('aria-activedescendant');
    }
    paintSearchActiveIndex = -1;
  }

  function syncPaintSearchValue() {
    if (inputPaintName) inputPaintName.value = selectedPaint?.name || '';
  }

  function setPaintSearchEnabled(enabled, { loading = false } = {}) {
    if (!inputPaintName) return;
    inputPaintName.disabled = !enabled;
    inputPaintName.placeholder = loading ? 'Loading paint names…' : 'Search paint name';
    if (!enabled) closePaintSearch();
  }

  function renderPaintSearchResults(query = '') {
    if (!paintSearchList || !inputPaintName || inputPaintName.disabled || !makeSel?.value) return [];
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
      closePaintSearch();
      paintSearchList.innerHTML = '';
      return [];
    }
    const matches = makePaintSearchCatalog
      .filter(record => !normalized || record.name.toLowerCase().includes(normalized))
      .slice(0, 50);
    paintSearchList.innerHTML = '';
    matches.forEach((record, index) => {
      const option = document.createElement('button');
      option.type = 'button';
      option.id = `${optionIdPrefix}-paint-option-${index}`;
      option.className = 'vf-combobox__option vf-paint-search__option';
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.dataset.paintSearchIndex = String(makePaintSearchCatalog.indexOf(record));
      const reference = paintSearchReference(record);
      option.innerHTML = `<span class="vf-paint-search__option-name">${escapeHtml(record.name)}</span><span class="vf-paint-search__option-meta">${escapeHtml(record.code ? `Paint code ${record.code}` : 'Factory paint')}</span>${reference ? `<span class="vf-paint-search__option-reference">${escapeHtml(reference)}</span>` : ''}`;
      option.addEventListener('mousedown', event => event.preventDefault(), { signal: lifecycleSignal });
      option.addEventListener('click', () => selectPaintSearchResult(record), { signal: lifecycleSignal });
      paintSearchList.appendChild(option);
    });
    if (!matches.length) {
      const empty = document.createElement('div');
      empty.className = 'vf-combobox__empty';
      empty.textContent = 'No matching paints for this vehicle make';
      paintSearchList.appendChild(empty);
    }
    paintSearchList.hidden = false;
    inputPaintName.setAttribute('aria-expanded', 'true');
    paintSearchActiveIndex = -1;
    return matches;
  }

  function setActivePaintSearchOption(nextIndex) {
    if (!paintSearchList || !inputPaintName) return;
    const options = Array.from(paintSearchList.querySelectorAll('.vf-paint-search__option'));
    if (!options.length) return;
    paintSearchActiveIndex = Math.max(0, Math.min(nextIndex, options.length - 1));
    options.forEach((option, index) => {
      const active = index === paintSearchActiveIndex;
      option.classList.toggle('vf-combobox__option--active', active);
      option.setAttribute('aria-selected', String(active));
    });
    inputPaintName.setAttribute('aria-activedescendant', options[paintSearchActiveIndex].id);
    options[paintSearchActiveIndex].scrollIntoView({ block: 'nearest' });
  }

  async function loadPaintIndex() {
    const url = root.dataset.paintIndexUrl;
    const json = await shared.paintIndexCache.get(url, async () => {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    }, value => Boolean(value && Array.isArray(value.makes) && value.makes.length && value.specialPaintPrograms && typeof value.specialPaintPrograms === 'object'));
    if (!requestCoordinator.isActive()) return false;
    paintRegistry = {
      makes: json.makes,
      technicalPaintSources: json.technicalPaintSources || [],
      specialPaintPrograms: json.specialPaintPrograms,
    };
    registryLoaded = true;
    populateMakes(paintRegistry.makes);
    updateVehicleMakeIntroduction();
    return true;
  }

  async function initializeLockedMake() {
    if (!lockMake) return true;

    if (makeCombobox) makeCombobox.hidden = true;
    if (lockedMake) lockedMake.hidden = false;
    if (lockedMakeValue) lockedMakeValue.textContent = defaultMake || 'Vehicle make unavailable';

    if (!defaultMake || !paintRegistry.makes.includes(defaultMake)) {
      root.dataset.makeUnavailable = 'true';
      if (lockedMakeValue) lockedMakeValue.textContent = defaultMake ? `${defaultMake} — unavailable` : 'Vehicle make unavailable';
      if (paintUnavailable) {
        paintUnavailable.style.display = '';
        paintUnavailable.textContent = 'This vehicle make configuration is currently unavailable.';
      }
      if (window.Shopify?.designMode) {
        console.warn(`VeloForge: Locked vehicle make "${defaultMake || '(blank)'}" is not present in the public paint registry.`);
      }
      updateSummaryVehicle();
      updateReadiness();
      return false;
    }

    delete root.dataset.makeUnavailable;
    makeSel.value = defaultMake;
    syncComboboxValue('make');
    return onMakeChange();
  }

  function populateMakes(makes) {
    makeSel.innerHTML = '<option value="">Select Make</option>' +
      makes.map(m => `<option value="${escapeHtml(m)}">${escapeHtml(m)}</option>`).join('');
    syncComboboxValue('make');
    configureSecondaryField();
  }

  async function onMakeChange() {
    const request = requestCoordinator.beginMake();
    const make = makeSel.value;
    editingMake = false;
    syncComboboxValue('make');
    configureSecondaryField();
    clearPaints();
    makePaintSearchCatalog = [];
    setPaintSearchEnabled(false, { loading: Boolean(make) });
    if (!make) {
      updateSummaryVehicle();
      updateReadiness();
      syncConfigurationUrl();
      return requestCoordinator.isCurrent(request);
    }

    let makeData;
    try {
      makeData = await loadMakeData(make);
    } catch (error) {
      if (!requestCoordinator.isCurrent(request)) return false;
      warnInDevelopmentOnce(`paint-data-load:${makeSlug(make)}`, `Could not load paint data for ${make}; a later selection can retry.`);
      paintUnavailable.style.display = '';
      paintUnavailable.textContent = 'Paint colors could not load. Please try again, or use “don’t see your paint color listed?” above.';
      updateSummaryVehicle();
      updateReadiness();
      return false;
    }
    if (!requestCoordinator.isCurrent(request) || makeSel.value !== make) return false;
    const years = getYearsForMake(makeData, make);
    const programs = getSpecialPaintPrograms(make);
    const beforeYears = programs.filter(program => program.position === 'before-years');
    const afterYears = programs.filter(program => program.position !== 'before-years');
    const renderProgramOption = program => `<option value="${escapeHtml(program.value)}">${escapeHtml(program.label)}</option>`;
    yearSel.innerHTML += beforeYears.map(renderProgramOption).join('');
    yearSel.innerHTML += years.map(value => `<option value="${escapeHtml(value)}">${escapeHtml(value)}</option>`).join('');
    yearSel.innerHTML += afterYears.map(renderProgramOption).join('');
    makePaintSearchCatalog = await buildMakePaintSearchCatalog(make, makeData);
    if (!requestCoordinator.isCurrent(request) || makeSel.value !== make) return false;
    setPaintSearchEnabled(makePaintSearchCatalog.length > 0);
    updateSummaryVehicle();
    updateReadiness();
    syncConfigurationUrl();
    return true;
  }

  async function onYearChange() {
    const request = requestCoordinator.beginPaint();
    const make = makeSel.value;
    const secondaryValue = yearSel.value;
    syncComboboxValue('year');
    clearPaints();
    if (!make || !secondaryValue) {
      updateSummaryVehicle();
      updateReadiness();
      syncConfigurationUrl();
      return requestCoordinator.isCurrent(request);
    }

    const program = getSelectedPaintProgram(make, secondaryValue);
    let paintData;
    let displayNameLookup = {};
    try {
      paintData = program ? await loadPaintData(program) : await loadMakeData(make);
      if (program?.displayNameLookup) {
        try {
          displayNameLookup = await loadDisplayNameLookup(program);
        } catch (error) {
          if (!requestCoordinator.isCurrent(request)) return false;
          warnInDevelopmentOnce(`lookup-load:${program.displayNameLookup}`, `Could not load display-name lookup ${program.displayNameLookup}; source names will be used and a later selection can retry.`);
        }
      }
    } catch (error) {
      if (!requestCoordinator.isCurrent(request)) return false;
      warnInDevelopmentOnce(`paint-selection-load:${makeSlug(make)}:${secondaryValue}`, `Could not load the selected paint catalog; a later selection can retry.`);
      paintUnavailable.style.display = '';
      paintUnavailable.textContent = 'Paint colors could not load. Please try again, or use “don’t see your paint color listed?” above.';
      updateSummaryVehicle();
      updateReadiness();
      return false;
    }
    if (!requestCoordinator.isCurrent(request) || makeSel.value !== make || yearSel.value !== secondaryValue) return false;
    const paints = program
      ? applyReviewedDisplayNames(getPaintsForProgram(paintData, program), program, displayNameLookup)
      : getPaintsForMakeYear(paintData, secondaryValue);
    if (paints?.length) {
      renderPaints(paints, { sortByFamily: Boolean(program) });
    } else {
      paintUnavailable.style.display = '';
    }
    updateSummaryVehicle();
    updateReadiness();
    syncConfigurationUrl();
    return true;
  }

  /* ===== PAINT SWATCHES ===== */
  function clearPaints() {
    showGallerySlide(0);
    if (paintGrid) paintGrid.innerHTML = '';
    if (paintUnavailable) paintUnavailable.style.display = 'none';
    selectedPaint = null;
    setColor(null);
    if (inputPaintName) inputPaintName.value = '';
    if (inputPaintCode) inputPaintCode.value = '';
    if (inputPaintHex) inputPaintHex.value = '';
    updateSummaryPaint();
  }

  function renderPaints(paints, { sortByFamily = false } = {}) {
    if (!paintGrid) return;
    const renderSwatch = (paint, index) => {
      const customerDisplayName = paint.customerDisplayName || paint.display_name;
      const sourceDisplayName = paint.sourceDisplayName || paint.display_name;
      const paintCode = paint.paint_code || '';
      const stableId = paint.porsche_asset_id ?? paint.slug ?? '';
      const accessibleName = paintCode ? `${customerDisplayName}, paint code ${paintCode}` : customerDisplayName;
      return `\n      <button type="button" class="vf-paint-swatch" role="radio" aria-checked="false" aria-label="${escapeHtml(accessibleName)}" tabindex="${index === 0 ? '0' : '-1'}" data-paint-index="${index}" data-stable-id="${escapeHtml(stableId)}" data-hex="${paint.hex_value}" data-name="${escapeHtml(customerDisplayName)}" data-source-name="${escapeHtml(sourceDisplayName)}" data-code="${escapeHtml(paintCode)}">\n        <span class="vf-paint-swatch__color" aria-hidden="true" style="background:${paint.hex_value}"></span>\n        <span class="vf-paint-swatch__info">\n          <span class="vf-paint-swatch__name">${escapeHtml(customerDisplayName)}</span>\n          <span class="vf-paint-swatch__code">${escapeHtml(paintCode)}</span>\n        </span>\n      </button>\n    `;
    };

    const familyOrder = {
      green: 1, 'grün': 1, grun: 1, grxfcn: 1,
      gray: 2, grey: 2, grau: 2,
      blue: 3, blau: 3,
      black: 4, schwarz: 4,
      violet: 5, purple: 5, violett: 5,
      brown: 6, braun: 6,
      red: 7, rot: 7,
      orange: 8,
      yellow: 9, gelb: 9,
      white: 10, weiss: 10, 'weiß': 10, weixdf: 10,
    };
    const getFamilyOrder = paint => {
      const suppliedOrder = Number(paint.color_family_order);
      if (Number.isFinite(suppliedOrder) && suppliedOrder > 0) return suppliedOrder;
      const family = String(paint.color_family || '').toLowerCase().replace(/[^a-zäöüß]+/g, '');
      return familyOrder[family] || 999;
    };
    const displayPaints = sortByFamily
      ? [...paints].sort((left, right) =>
          getFamilyOrder(left) - getFamilyOrder(right) ||
          left.display_name.localeCompare(right.display_name) ||
          left.paint_code.localeCompare(right.paint_code))
      : paints;

    paintGrid.innerHTML = displayPaints.map(renderSwatch).join('');
    syncRadioGroup(paintGrid);

    paintGrid.querySelectorAll('.vf-paint-swatch').forEach(btn => {
      btn.addEventListener('click', () => selectSwatch(btn), { signal: lifecycleSignal });
    });
  }

  function applyPaintSelection(paint, activeButton = null) {
    if (!paint) return;
    paintGrid.querySelectorAll('.vf-paint-swatch').forEach(b => b.classList.remove('vf-paint-swatch--active'));
    if (activeButton) activeButton.classList.add('vf-paint-swatch--active');
    syncRadioGroup(paintGrid, activeButton);
    selectedPaint = {
      stableId: String(paint.stableId || ''),
      hex: String(paint.hex || ''),
      name: String(paint.name || ''),
      code: String(paint.code || ''),
      sourceDisplayName: String(paint.sourceDisplayName || paint.name || ''),
      customerDisplayName: String(paint.customerDisplayName || paint.name || ''),
      shareable: true,
    };
    if (inputPaintName) inputPaintName.value = selectedPaint.name;
    if (inputPaintCode) inputPaintCode.value = selectedPaint.code;
    if (inputPaintHex) inputPaintHex.value = selectedPaint.hex;
    setColor(selectedPaint.hex);
    showGallerySlide(0);
    closePaintSearch();
    updateSummaryPaint();
    updateReadiness();
    syncConfigurationUrl();
  }

  function selectSwatch(btn) {
    if (!paintGrid || !btn) return;
    applyPaintSelection({
      stableId: btn.dataset.stableId || '',
      hex: btn.dataset.hex,
      name: btn.dataset.name,
      code: btn.dataset.code,
      sourceDisplayName: btn.dataset.sourceName || btn.dataset.name,
      customerDisplayName: btn.dataset.name,
    }, btn);
  }

  function selectPaintSearchResult(record) {
    if (!record || !makeSel?.value) return;
    const currentSecondary = yearSel?.value || '';
    const compatible = !currentSecondary || record.years.includes(currentSecondary) || record.programValues.includes(currentSecondary);
    if (!compatible && yearSel) {
      yearSel.value = '';
      syncComboboxValue('year');
      if (paintGrid) paintGrid.innerHTML = '';
      if (paintUnavailable) paintUnavailable.style.display = 'none';
    }
    const matchingButton = Array.from(paintGrid?.querySelectorAll('.vf-paint-swatch') || []).find(button =>
      paintSearchIdentity({ customerDisplayName: button.dataset.name, paint_code: button.dataset.code, hex_value: button.dataset.hex }) ===
      paintSearchIdentity({ customerDisplayName: record.name, paint_code: record.code, hex_value: record.hex })
    );
    applyPaintSelection(record, matchingButton || null);
    updateSummaryVehicle();
    saveState();
  }

  /* Manual paint inputs */
  function onManualPaintInput() {
    const name = inputPaintName?.value.trim() || '';
    const code = inputPaintCode?.value.trim() || '';
    const hexRaw = inputPaintHex?.value.trim() || '';
    if (hexRaw) {
      const normalizedHex = hexRaw.startsWith('#') ? hexRaw : '#' + hexRaw;
      selectedPaint = { hex: normalizedHex, name: name || 'Custom', code: code || '' };
      setColor(normalizedHex);
      showGallerySlide(0);
      if (paintGrid) {
        paintGrid.querySelectorAll('.vf-paint-swatch').forEach(b => b.classList.remove('vf-paint-swatch--active'));
        syncRadioGroup(paintGrid);
      }
    } else {
      selectedPaint = null;
    }
    updateSummaryPaint();
    updateReadiness();
    syncConfigurationUrl();
  }

  if (inputPaintCode) inputPaintCode.addEventListener('input', onManualPaintInput, { signal: lifecycleSignal });
  if (inputPaintHex) inputPaintHex.addEventListener('input', onManualPaintInput, { signal: lifecycleSignal });

  inputPaintName?.addEventListener('focus', () => {
    if (!inputPaintName.disabled && inputPaintName.value !== selectedPaint?.name) renderPaintSearchResults(inputPaintName.value);
  }, { signal: lifecycleSignal });
  inputPaintName?.addEventListener('click', () => {
    if (!inputPaintName.disabled) {
      if (selectedPaint) inputPaintName.select();
    }
  }, { signal: lifecycleSignal });
  inputPaintName?.addEventListener('input', () => {
    if (!inputPaintName.disabled) renderPaintSearchResults(inputPaintName.value);
  }, { signal: lifecycleSignal });
  inputPaintName?.addEventListener('keydown', event => {
    if (!paintSearchList || inputPaintName.disabled) return;
    const options = Array.from(paintSearchList.querySelectorAll('.vf-paint-search__option'));
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (paintSearchList.hidden) renderPaintSearchResults(inputPaintName.value);
      setActivePaintSearchOption(paintSearchActiveIndex + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActivePaintSearchOption(paintSearchActiveIndex <= 0 ? options.length - 1 : paintSearchActiveIndex - 1);
    } else if (event.key === 'Enter' && !paintSearchList.hidden) {
      event.preventDefault();
      const chosen = options[Math.max(paintSearchActiveIndex, 0)];
      const record = chosen ? makePaintSearchCatalog[Number(chosen.dataset.paintSearchIndex)] : null;
      if (record) selectPaintSearchResult(record);
    } else if (event.key === 'Escape') {
      closePaintSearch();
      syncPaintSearchValue();
    }
  }, { signal: lifecycleSignal });
  inputPaintName?.addEventListener('blur', () => {
    const timer = window.setTimeout(() => {
      rootTimers.delete(timer);
      if (destroyed || !root.isConnected) return;
      closePaintSearch();
      syncPaintSearchValue();
    }, 100);
    rootTimers.add(timer);
  }, { signal: lifecycleSignal });

  /* ===== RING COLOR COMPOSITING ===== */
  function applyRingMask() {
    if (!ringInlayMask) return;
    const maskValue = `url("${ringInlayMask}")`;
    [cwDesktop, tDesktop, hDesktop, cwMobile, tMobile, hMobile]
      .filter(Boolean)
      .forEach((layer) => {
        try { layer.style.setProperty('--vf-ring-inlay-mask', maskValue); } catch (_) {}
      });
  }

  function setColor(hex) {
    const groups = [ [cwDesktop, tDesktop, hDesktop], [cwMobile, tMobile, hMobile] ];
    groups.forEach(([cw, t, h]) => {
      if (!cw) return;
      if (hex) {
        cw.style.backgroundColor = hex;
        if (t) t.style.backgroundColor = hex;
        if (h) h.style.backgroundColor = '#ffffff';
        // Support both desktop and mobile preview attribute names
        cw.closest('[data-ring-preview],[data-ring-preview-mobile]')?.classList.add('vf-ring-preview--active');
      } else {
        cw.style.backgroundColor = '';
        if (t) t.style.backgroundColor = '';
        if (h) h.style.backgroundColor = '';
        cw.closest('[data-ring-preview],[data-ring-preview-mobile]')?.classList.remove('vf-ring-preview--active');
      }
    });
  }

  /* Neutral preview */
  if (neutralBtn) {
    neutralBtn.addEventListener('click', () => {
      setColor(null);
      showGallerySlide(0);
      if (paintGrid) {
        paintGrid.querySelectorAll('.vf-paint-swatch').forEach(b => b.classList.remove('vf-paint-swatch--active'));
        syncRadioGroup(paintGrid);
      }
      selectedPaint = null;
      if (inputPaintName) inputPaintName.value = '';
      if (inputPaintCode) inputPaintCode.value = '';
      if (inputPaintHex) inputPaintHex.value = '';
      updateSummaryPaint();
      syncConfigurationUrl();
    }, { signal: lifecycleSignal });
  }

  /* ===== SIZE SELECTOR ===== */
  function isResolvedBackingVariant(variantId) {
    const id = String(variantId || '');
    if (!usesInPlaceConfiguration) return Boolean(id && allowedBackingVariantIds.has(id));
    return Boolean(
      id &&
      allowedBackingVariantIds.has(id) &&
      backingVariantIndex.has(activeConfiguration?.id, id) &&
      activeConfiguration?.product?.variants?.some(variant => String(variant.id) === id && variant.available)
    );
  }

  function resolveConfigurationVariant(configuration, size) {
    if (!configuration || !size) return null;
    if (configuration.merchandiseArchitecture === 'series-product') {
      return selectedOptionsVariantResolver?.resolve({
        Metal: configuration.metalLabel,
        Karat: configuration.karatLabel,
        Size: size,
      }) || null;
    }
    const variantId = variantMap[size];
    return configuration.product?.variants?.find(variant => String(variant.id) === String(variantId)) || null;
  }

  function selectSizeButton(btn, { emitEvent = true } = {}) {
      showGallerySlide(0);
      sizeGrid.querySelectorAll('.vf-size-btn').forEach(b => b.classList.remove('vf-size-btn--active'));
      btn.classList.add('vf-size-btn--active');
      syncRadioGroup(sizeGrid, btn);
      selectedSize = btn.dataset.sizeValue;
      const resolvedVariant = resolveConfigurationVariant(activeConfiguration, selectedSize);
      const vid = resolvedVariant?.id || '';
      if (variantHidden) variantHidden.value = isResolvedBackingVariant(vid) ? vid : '';
      updateHiddenProps();
      if (emitEvent && activeConfiguration?.product) {
        emitVariantChange(activeConfiguration.product, resolvedVariant);
      }
      updateReadiness();
      syncConfigurationUrl();
  }

  function bindSizeButtons() {
    sizeGrid?.querySelectorAll('.vf-size-btn')?.forEach(btn => {
      btn.addEventListener('click', () => selectSizeButton(btn), { signal: lifecycleSignal });
    });
  }

  bindSizeButtons();

  function updateConfigurationButtons(configuration) {
    root.querySelectorAll('[data-configuration-metal]').forEach(button => {
      const active = button.dataset.configurationMetal === configuration.metalKey;
      button.classList.toggle('vf-option-btn--active', active);
      button.setAttribute('aria-checked', String(active));
      button.tabIndex = active ? 0 : -1;
    });
    root.querySelectorAll('[data-configuration-karat]').forEach(button => {
      const active = button.dataset.configurationKarat === configuration.karatKey;
      button.classList.toggle('vf-option-btn--active', active);
      button.setAttribute('aria-checked', String(active));
      button.tabIndex = active ? 0 : -1;
    });
  }

  function rebuildProductGallery(product) {
    const viewport = desktopGallery?.querySelector('.vf__gallery-viewport');
    const previewSlide = viewport?.querySelector('[data-gallery-index="0"]');
    if (!viewport || !previewSlide) return;
    viewport.querySelectorAll('[data-gallery-slide]:not([data-gallery-index="0"])').forEach(slide => slide.remove());
    const arrow = viewport.querySelector('[data-gallery-previous]');
    (product.media || []).filter(media => !media.internalPreviewAsset && !/paint inlay layer/i.test(media.alt || '')).forEach((media, index) => {
      const slide = document.createElement('div');
      slide.className = 'vf__gallery-slide';
      slide.dataset.gallerySlide = '';
      slide.dataset.galleryIndex = String(index + 1);
      slide.setAttribute('aria-hidden', 'true');
      const displayTitle = root.dataset.displayProductTitle || product.title;
      slide.setAttribute('aria-label', media.alt || displayTitle);
      slide.hidden = true;
      const wrapper = document.createElement('div');
      wrapper.className = 'vf__gallery-slide-image';
      const image = document.createElement('img');
      image.src = media.url;
      image.alt = media.alt || displayTitle;
      image.loading = 'lazy';
      if (media.width) image.width = media.width;
      if (media.height) image.height = media.height;
      wrapper.appendChild(image);
      slide.appendChild(wrapper);
      viewport.insertBefore(slide, arrow || null);
    });
    gallerySlides = Array.from(root.querySelectorAll('[data-gallery-slide]'));
    showGallerySlide(0);
  }

  function switchRingConfiguration(configuration) {
    if (!configuration || configuration.id === activeConfiguration?.id) return;
    const previousSize = selectedSize;
    const product = configuration.product;
    activeConfiguration = configuration;
    selectedOptionsVariantResolver = configuration.merchandiseArchitecture === 'series-product'
      ? window.VeloForgeConfiguratorContract.createSelectedOptionsVariantResolver(product)
      : null;
    root.dataset.activeConfiguration = configuration.id;
    root.dataset.activeProductId = product.id;
    root.dataset.metalFamily = configuration.metalKey;
    if (productTitle) productTitle.textContent = root.dataset.displayProductTitle || product.title;

    ringBaseImages.forEach(image => {
      image.src = product.ringBase;
      if (image.alt) image.alt = root.dataset.displayProductTitle || product.title;
    });
    root.dataset.ringBase = product.ringBase;
    rebuildProductGallery(product);

    variantMap = Object.fromEntries((product.variants || []).filter(variant => variant.available).map(variant => [variant.title, variant.id]));
    if (sizeGrid) {
      sizeGrid.replaceChildren();
      (product.variants || []).forEach(variant => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'vf-size-btn';
        button.setAttribute('role', 'radio');
        button.setAttribute('aria-checked', 'false');
        button.tabIndex = -1;
        button.dataset.sizeValue = variant.title;
        button.dataset.variantIdValue = variant.id;
        button.textContent = variant.title;
        button.disabled = !variant.available;
        if (!variant.available) button.setAttribute('aria-disabled', 'true');
        sizeGrid.appendChild(button);
      });
      syncRadioGroup(sizeGrid);
      bindSizeButtons();
    }

    selectedSize = null;
    if (variantHidden) variantHidden.value = '';
    if (previousSize && variantMap[previousSize]) {
      const matchingSize = Array.from(sizeGrid?.querySelectorAll('.vf-size-btn') || []).find(button => button.dataset.sizeValue === previousSize);
      if (matchingSize) selectSizeButton(matchingSize, { emitEvent: false });
    }
    updateConfigurationButtons(configuration);
    updateHiddenProps();
    updateReadiness();
    if (configuratorContext === 'vehicle_make' || isConfigurationSelectionComplete()) {
      emitProductChange(product);
    }
  }

  function selectConfigurationPart(type, value) {
    if (!activeConfiguration) return;
    const metalKey = type === 'metal' ? value : activeConfiguration.metalKey;
    const karatKey = type === 'karat' ? value : activeConfiguration.karatKey;
    const match = ringConfigurations.find(configuration => configuration.metalKey === metalKey && configuration.karatKey === karatKey);
    if (match) switchRingConfiguration(match);
  }

  function isConfigurationSelectionComplete() {
    return window.VeloForgeConfiguratorContract.isConfigurationSelectionComplete({
      metalSelected: intentionalMetalSelected,
      karatSelected: intentionalKaratSelected,
    });
  }

  function syncConfigurationSelectionState() {
    const complete = isConfigurationSelectionComplete();
    root.dataset.configurationSelectionComplete = String(complete);
    if (usesInPlaceConfiguration) {
      const pricePresentation = window.VeloForgeConfiguratorContract.resolveConfigurationPrice({
        selectionComplete: complete,
        startingPrice: root.dataset.startingPrice,
        configuration: activeConfiguration,
      });
      if (productPricePrefix) productPricePrefix.hidden = !pricePresentation.showStartingPrice;
      if (productPrice) productPrice.textContent = pricePresentation.price;
    }
    if (configuratorContext === 'ring_product' && complete && activeConfiguration?.product) emitProductChange(activeConfiguration.product);
  }

  root.querySelectorAll('[data-configuration-metal]').forEach(button => {
    button.addEventListener('click', event => {
      const selectedMetal = String(button.dataset.configurationMetal || '').trim().toLowerCase();
      const activeMetal = String(activeConfiguration?.metalKey || '').trim().toLowerCase();
      const metalChanged = Boolean(selectedMetal && selectedMetal !== activeMetal);
      const shouldAnimateMetalChange = event.isTrusted && initialRestoreComplete && !applyingUrlState && metalChanged;
      intentionalMetalSelected = true;
      selectConfigurationPart('metal', selectedMetal);
      syncConfigurationSelectionState();
      syncConfigurationUrl();
      if (shouldAnimateMetalChange) animateBuilderSubtitleMetal(selectedMetal);
    }, { signal: lifecycleSignal });
  });
  root.querySelectorAll('[data-configuration-karat]').forEach(button => {
    button.addEventListener('click', () => {
      intentionalKaratSelected = true;
      selectConfigurationPart('karat', button.dataset.configurationKarat);
      syncConfigurationSelectionState();
      syncConfigurationUrl();
    }, { signal: lifecycleSignal });
  });

  syncConfigurationSelectionState();

  /* ===== QUANTITY ===== */
  if (decreaseBtn) decreaseBtn.addEventListener('click', () => { if (qty > 1) { qty--; updateQty(); syncConfigurationUrl(); } }, { signal: lifecycleSignal });
  if (increaseBtn) increaseBtn.addEventListener('click', () => { if (qty < 99) { qty++; updateQty(); syncConfigurationUrl(); } }, { signal: lifecycleSignal });

  function updateQty() {
    qtyValue.textContent = qty;
    if (qtyInput) qtyInput.value = qty;
    if (decreaseBtn) decreaseBtn.disabled = qty <= 1;
  }

  /* ===== SUMMARY + HIDDEN PROPS ===== */
  function updateSummaryPaint() {
    const text = selectedPaint ? `${selectedPaint.name}${selectedPaint.code ? ' (' + selectedPaint.code + ')' : ''}` : '—';
    if (summaryPaint) summaryPaint.textContent = text;
    updateBuilderPresentation();
    updateHiddenProps();
  }

  function updateSummaryVehicle() {
    const selectedSecondary = yearSel.options[yearSel.selectedIndex];
    const secondaryLabel = yearSel.value && selectedSecondary ? selectedSecondary.textContent : '';
    const parts = [makeSel.value, secondaryLabel].filter(Boolean);
    const text = parts.length ? parts.join(' ') : '—';
    if (summaryVehicle) summaryVehicle.textContent = text;
    updateBuilderPresentation();
    updateHiddenProps();
  }

  function updateHiddenProps() {
    const setPropName = (input, name) => {
      if (!input) return;
      input.name = name;
    };

    const setPropValue = (input, value) => {
      if (!input) return;
      input.value = value;
    };

    const composePaintName = () => {
      return (selectedPaint && selectedPaint.name) || inputPaintName?.value.trim() || '';
    };

    const getNotesSnapshot = () => {
      if (savedNotesData) return savedNotesData;
      return parseNotesText(savedNotes || '');
    };

    const notesSnapshot = getNotesSnapshot();
    const structuredNotesSnapshot = savedNotesData || null;
    const notesRaw = notesSnapshot.raw || savedNotes || '';
    const selectedSecondary = yearSel.options[yearSel.selectedIndex];
    const secondaryLabel = yearSel.value && selectedSecondary ? selectedSecondary.textContent : '';
    const vehicleValue = [notesSnapshot.make, notesSnapshot.year].filter(Boolean).join(' ') || [makeSel.value, secondaryLabel].filter(Boolean).join(' ');
    const paintNameValue = structuredNotesSnapshot?.paintName || composePaintName();
    const paintCodeValue = structuredNotesSnapshot?.paintCode || (selectedPaint && selectedPaint.code) || inputPaintCode?.value.trim() || '';
    const previewPaintValue = structuredNotesSnapshot?.paint || notesSnapshot.paint || paintNameValue;

    if (propSeries) {
      setPropName(propSeries, 'properties[Series]');
      setPropValue(propSeries, root.dataset.ringSeriesName || '');
    }
    if (propMetal) {
      setPropName(propMetal, 'properties[Metal]');
      setPropValue(propMetal, activeConfiguration?.metalLabel || '');
    }
    if (propKarat) {
      setPropName(propKarat, 'properties[Karat]');
      setPropValue(propKarat, activeConfiguration?.karatLabel || '');
    }
    if (propSize) {
      setPropName(propSize, 'properties[Size]');
      setPropValue(propSize, selectedSize || '');
    }
    if (propPaintName) {
      setPropName(propPaintName, 'properties[Paint]');
      setPropValue(propPaintName, paintNameValue);
    }
    if (propPaintCode) {
      setPropName(propPaintCode, 'properties[Paint Code]');
      // Persist paint code from selected swatch or manual input
      setPropValue(propPaintCode, paintCodeValue);
    }
    if (propHex) {
      setPropName(propHex, 'properties[_Hex]');
      // Persist hex from selected swatch or manual input
      setPropValue(propHex, (selectedPaint && selectedPaint.hex) || (inputPaintHex?.value.trim() || ''));
    }
    if (propOtherDetails) {
      setPropName(propOtherDetails, 'properties[Other Details]');
      setPropValue(propOtherDetails, notesSnapshot.otherDetails || '');
    }
    if (propVehicle) {
      setPropName(propVehicle, 'properties[Vehicle]');
      setPropValue(propVehicle, vehicleValue);
    }
    if (propModel) {
      setPropName(propModel, 'properties[Model]');
      setPropValue(propModel, '');
    }
    if (propNotes) {
      setPropName(propNotes, notesSnapshot.make || notesSnapshot.year || notesSnapshot.paint || notesSnapshot.otherDetails ? 'properties[Notes]' : 'properties[_Notes]');
      setPropValue(propNotes, notesSnapshot.make || notesSnapshot.year || notesSnapshot.paint || notesSnapshot.otherDetails ? 'Paint color not found - custom' : '');
    }
    if (propNotesFallback) {
      setPropName(propNotesFallback, 'properties[_Notes]');
      setPropValue(propNotesFallback, notesRaw);
    }
    if (propPreviewImage) {
      setPropName(propPreviewImage, 'properties[_Preview Image]');
      // Build preview URL. If no preview endpoint is configured, fall back to data-url SVG.
      const hexVal = (selectedPaint && selectedPaint.hex) || (inputPaintHex?.value.trim() || '');
      const previewEndpoint = root.dataset.previewEndpoint || '';
      const ringInlay = root.dataset.ringInlay || '';
      const ringBase = root.dataset.ringBase || '';

      const slugify = (s) => {
        if (!s) return '';
        return String(s)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
      };

      const buildDataSvg = (hex) => {
        const hexClean = hex ? (hex.startsWith('#') ? hex : `#${hex}`) : 'transparent';
        const svg = `<?xml version="1.0" encoding="UTF-8"?>` +
          `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 800" width="800" height="800">` +
          `<defs><mask id="m"><image href="${ringInlay}" x="0" y="0" width="800" height="800" preserveAspectRatio="xMidYMid slice" /></mask></defs>` +
          `<image href="${ringInlay}" x="0" y="0" width="800" height="800" preserveAspectRatio="xMidYMid slice" />` +
          `<rect x="0" y="0" width="800" height="800" fill="${hexClean}" mask="url(#m)" />` +
          `<image href="${ringBase}" x="0" y="0" width="800" height="800" preserveAspectRatio="xMidYMid slice" />` +
          `</svg>`;
        return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
      };

      // Prefer a theme asset named preview-{slug}.svg (no external hosting required).
      const codeOrName = (selectedPaint && (selectedPaint.code || selectedPaint.name)) || inputPaintCode?.value.trim() || inputPaintName?.value.trim() || '';
      const slug = slugify(codeOrName || previewPaintValue || 'preview');
      const assetUrl = `/assets/preview-${slug}.svg`;

      // If a preview endpoint is configured, use it as a fallback; otherwise use inline SVG data URI.
      if (previewEndpoint) {
        const params = new URLSearchParams({ hex: hexVal || '', ring_inlay: ringInlay, ring_base: ringBase });
        setPropValue(propPreviewImage, assetUrl);
      } else {
        // Use the asset URL (you can upload matching files to /assets/preview-{slug}.svg). If missing, email clients may still show properties text.
        setPropValue(propPreviewImage, assetUrl);
      }
    }
    if (propConfigurationUrl) {
      setPropName(propConfigurationUrl, 'properties[_Configuration URL]');
      setPropValue(propConfigurationUrl, buildCurrentConfigurationUrl().toString());
    }
    // DEV: log current prop values to help debug why properties may not persist
    try {
      /* eslint-disable no-console */
      console.log('vf:updateHiddenProps', {
        Paint: propPaintName?.value,
        PaintCode: propPaintCode?.value,
        Hex: propHex?.value,
        Vehicle: propVehicle?.value,
        Notes: propNotes?.value,
      });
      /* eslint-enable no-console */
    } catch (_) {}
  }

  function updateReadiness() {
    if (!addBtn) return;
    const hasSize = Boolean(selectedSize && isResolvedBackingVariant(variantHidden?.value));
    const hasConfiguration = Boolean(activeConfiguration?.product && (!usesInPlaceConfiguration || isConfigurationSelectionComplete()));
    const hasVehicle = Boolean(makeSel.value);
    const hasPaint = Boolean(selectedPaint);
    const ready = hasConfiguration && hasSize && (savedNotes || (hasVehicle && hasPaint));
    addBtn.dataset.ready = String(ready);
    addBtn.setAttribute('aria-disabled', String(!ready));
  }

  function missingConfigurationMessage() {
    if (usesInPlaceConfiguration && !isConfigurationSelectionComplete()) return 'Please select your metal and karat to continue.';
    if (!selectedSize) return 'Please select a ring size to continue.';
    if (!savedNotes && !makeSel.value) return 'Please select your vehicle make to continue.';
    if (!savedNotes && !selectedPaint) return 'Please select your factory paint color to continue.';
    if (!isResolvedBackingVariant(variantHidden?.value)) return 'Please select an available ring configuration to continue.';
    return '';
  }

  // Ensure hidden props are up-to-date when Add to cart is clicked (covers JS add-to-cart flows)
  if (addBtn) {
    addBtn.addEventListener('click', (e) => {
      const guidance = missingConfigurationMessage();
      if (guidance) {
        e.preventDefault();
        showStatus(guidance, { error: true });
        return;
      }
      try { updateHiddenProps(); } catch (err) { /* ignore */ }
    }, { signal: lifecycleSignal });
  }

  /* ===== FORM SUBMIT ===== */
  form?.addEventListener('submit', (e) => {
    if (!window.VeloForgeConfiguratorContract.ownsForm(root, form)) {
      e.preventDefault();
      warnInDevelopmentOnce('form-ownership', 'Configurator form ownership could not be verified; submission was stopped.');
      showStatus('This configuration could not be submitted. Please refresh and try again.');
      return;
    }
    updateHiddenProps();
    if (!isResolvedBackingVariant(variantHidden?.value)) {
      e.preventDefault();
      if (variantHidden) variantHidden.value = '';
      showStatus('Select a valid ring configuration and ring size.');
      return;
    }
    if (usesInPlaceConfiguration && !isConfigurationSelectionComplete()) {
      e.preventDefault();
      showStatus('Select your metal and karat.');
      return;
    }
    if (!selectedSize) {
      e.preventDefault(); showStatus('Please select a ring size.'); return;
    }
    if (!savedNotes) {
      if (!makeSel.value) { e.preventDefault(); showStatus('Select your vehicle make.'); return; }
      if (!selectedPaint) { e.preventDefault(); showStatus('Select your factory paint color.'); return; }
    }
  }, { signal: lifecycleSignal });

  function showStatus(msg, { error = false } = {}) {
    if (!statusEl || destroyed) return;
    if (statusTimeout) { window.clearTimeout(statusTimeout); rootTimers.delete(statusTimeout); }
    statusEl.textContent = msg;
    statusEl.classList.toggle('vf__status--error', error);
    const timer = window.setTimeout(() => {
      rootTimers.delete(timer);
      if (destroyed || !root.isConnected) return;
      statusEl.textContent = '';
      statusEl.classList.remove('vf__status--error');
      statusTimeout = 0;
    }, 4000);
    statusTimeout = timer;
    rootTimers.add(statusTimeout);
  }

  /* ===== STATE PERSISTENCE ===== */
  function saveState() {
    if (destroyed || !requestCoordinator.isActive()) return false;
    const state = {
      version: window.VeloForgeConfiguratorContract.SESSION_STATE_VERSION,
      savedAt: Date.now(),
      contextKey,
      contextType: root.dataset.configuratorContext || 'product',
      productId: String(root.dataset.activeProductId || ''),
      configurationId: usesInPlaceConfiguration ? String(activeConfiguration?.id || '') : '',
      configurationSelectionComplete: isConfigurationSelectionComplete(),
      make: makeSel?.value || '',
      secondaryValue: yearSel?.value || '',
      paint: selectedPaint ? window.VeloForgeConfiguratorContract.paintIdentity(selectedPaint) : null,
      size: selectedSize || '',
      quantity: qty,
      customPaint: savedNotes ? { saved: true, notes: savedNotes, notesData: savedNotesData || null } : null,
    };
    try {
      sessionStorage.setItem(STATE_KEY, JSON.stringify(state));
      sessionStorage.removeItem(LEGACY_STATE_KEY);
      sessionStorage.removeItem(LEGACY_NOTES_KEY);
      return true;
    } catch (_) { return false; }
  }

  async function restoreState() {
    try {
      try { sessionStorage.removeItem(LEGACY_STATE_KEY); sessionStorage.removeItem(LEGACY_NOTES_KEY); } catch (_) {}
      const specialValues = Object.values(paintRegistry.specialPaintPrograms || {}).flat().map(program => program.value);
      const state = window.VeloForgeConfiguratorContract.readSavedState(sessionStorage, STATE_KEY, {
        contextKey,
        isValidYear: value => window.VeloForgeYearValidation?.isValidVehicleYear(value) === true,
        specialValues,
      });
      if (!state || !requestCoordinator.isActive()) return false;

      if (usesInPlaceConfiguration && state.configurationId) {
        const configuration = ringConfigurations.find(item => String(item.id) === String(state.configurationId) && item.product);
        if (configuration) {
          switchRingConfiguration(configuration);
          if (state.configurationSelectionComplete === true) {
            intentionalMetalSelected = true;
            intentionalKaratSelected = true;
            syncConfigurationSelectionState();
          }
        }
      }

      // Restore vehicle selects if options exist
      if (!lockMake && state.make && makeSel.querySelector(`option[value="${CSSescape(state.make)}"]`)) {
        makeSel.value = state.make;
        const makeRestored = await onMakeChange();
        if (!makeRestored || makeSel.value !== state.make) return false;
      }
      if (state.secondaryValue && yearSel.querySelector(`option[value="${CSSescape(state.secondaryValue)}"]`)) {
        yearSel.value = state.secondaryValue;
        const yearRestored = await onYearChange();
        if (!yearRestored || yearSel.value !== state.secondaryValue) return false;
      }

      if (!requestCoordinator.isActive()) return false;

      // Restore paint if present
      if (state.paint) {
        const paintButtons = Array.from(paintGrid?.querySelectorAll('.vf-paint-swatch') || []);
        const match = window.VeloForgeConfiguratorContract.findSavedPaint(paintButtons.map(button => ({
          button,
          stableId: button.dataset.stableId,
          customerDisplayName: button.dataset.name,
          sourceDisplayName: button.dataset.sourceName,
          code: button.dataset.code,
          hex: button.dataset.hex,
        })), state.paint);
        if (match?.button) selectSwatch(match.button);
        else {
          const catalogMatch = findSavedPaintInSearchCatalog(state.paint);
          if (catalogMatch) selectPaintSearchResult(catalogMatch);
        }
      }

      // Restore size
      if (state.size) {
        const sizeBtn = sizeGrid.querySelector(`[data-size-value="${CSSescape(state.size)}"]`);
        if (sizeBtn && !sizeBtn.disabled && sizeBtn.getAttribute('aria-disabled') !== 'true') selectSizeButton(sizeBtn, { emitEvent: false });
      }

      qty = state.quantity;
      updateQty();

      const customPaint = state.customPaint;
      savedNotes = customPaint?.saved && typeof customPaint.notes === 'string' ? customPaint.notes : '';
      savedNotesData = customPaint?.saved && customPaint.notesData && typeof customPaint.notesData === 'object' ? customPaint.notesData : (savedNotes ? parseNotesText(savedNotes) : null);
      resetNotesForm();
      if (!savedNotes) {
        savedNotesData = null;
        if (notesForm) notesForm.style.display = 'none';
      }

      return true;
    } catch (e) {
      try { sessionStorage.removeItem(STATE_KEY); } catch (_) {}
      return false;
    }
  }

  /* ===== NOTES UX ===== */
  function buildNotesPrefill() {
    return {
      make: makeSel.value || '',
      year: yearSel.value || '',
      paintName: inputPaintName?.value || '',
      paintCode: inputPaintCode?.value || '',
      otherDetails: '',
    };
  }

  function openNotesEditor(prefill) {
    if (!notesForm || !notesPreview) return;
    
    // Reset vehicle and paint selections if opening a fresh editor (not editing existing note)
    if (prefill === undefined) {
      if (!lockMake) {
        makeSel.value = '';
        syncComboboxValue('make');
        configureSecondaryField();
      }
      clearPaints();
      hideVehicleGrid();
      updateSummaryVehicle();
      updateReadiness();
    }
    
    notesForm.style.display = '';
    notesPreview.style.display = 'none';
    const values = prefill === false
      ? buildNotesPrefill()
      : (prefill && typeof prefill === 'object' ? prefill : (savedNotesData || parseNotesText(savedNotes) || buildNotesPrefill()));

    if (notesMakeInput) notesMakeInput.value = values.make || '';
    if (lockMake && notesMakeInput) {
      notesMakeInput.value = defaultMake;
      notesMakeInput.readOnly = true;
    }
    if (notesYearInput) notesYearInput.value = values.year || '';
    if (notesPaintNameInput) notesPaintNameInput.value = values.paintName || '';
    if (notesPaintCodeInput) notesPaintCodeInput.value = values.paintCode || '';
    if (notesOtherDetailsInput) notesOtherDetailsInput.value = values.otherDetails || '';

    (notesMakeInput || notesYearInput || notesPaintNameInput || notesPaintCodeInput || notesOtherDetailsInput)?.focus();
  }

  function resetNotesForm() {
    if (notesMakeInput) notesMakeInput.value = '';
    if (notesYearInput) notesYearInput.value = '';
    if (notesPaintNameInput) notesPaintNameInput.value = '';
    if (notesPaintCodeInput) notesPaintCodeInput.value = '';
    if (notesOtherDetailsInput) notesOtherDetailsInput.value = '';
  }

  function showSavedNotePreview() {
    if (!notesPreview || !notesPreviewText) return;
    const hasSavedCustomInfo = !!savedNotes;
    notesPreviewText.textContent = hasSavedCustomInfo ? savedNotes : '';
    notesPreview.style.display = hasSavedCustomInfo ? '' : 'none';
    if (hasSavedCustomInfo) {
      if (root.querySelector('.vf__vehicle-grid')) hideVehicleGrid();
    } else {
      showVehicleGrid();
      resetNotesForm();
      if (notesForm) notesForm.style.display = 'none';
    }
    updateHiddenProps();
    updateReadiness();
  }

  function saveNotesFromEditor() {
    const parsed = {
      make: lockMake ? defaultMake : (notesMakeInput?.value.trim() || ''),
      year: notesYearInput?.value.trim() || '',
      paintName: notesPaintNameInput?.value.trim() || '',
      paintCode: notesPaintCodeInput?.value.trim() || '',
      otherDetails: notesOtherDetailsInput?.value.trim() || '',
    };

    if (parsed.make.length < 3) { alert('Make must be at least 3 characters.'); return; }
    if (!window.VeloForgeYearValidation?.isValidVehicleYear(parsed.year)) {
      const maximumYear = window.VeloForgeYearValidation?.getMaximumVehicleYear?.() || new Date().getFullYear() + 1;
      alert(`Enter a vehicle year from 1886 through ${maximumYear}.`);
      return;
    }
    if (!parsed.paintName && !parsed.paintCode) { alert('Please fill in Paint Color Name or Paint Code:'); return; }

    parsed.paint = [parsed.paintName, parsed.paintCode].filter(Boolean).join(' ');
    parsed.raw = buildNotesPreviewText(parsed);

    savedNotes = parsed.raw;
    savedNotesData = parsed;
    saveState();
    if (notesForm) notesForm.style.display = 'none';
    showSavedNotePreview();
  }

  function editSavedNote() { openNotesEditor(savedNotesData || parseNotesText(savedNotes)); }

  function deleteSavedNote() {
    if (!confirm('Delete saved note?')) return;
    savedNotes = '';
    savedNotesData = null;
    saveState();
    showSavedNotePreview();
  }

  function hideVehicleGrid() { const g = root.querySelector('.vf__vehicle-grid'); if (g) g.style.display = 'none'; }
  function showVehicleGrid() { const g = root.querySelector('.vf__vehicle-grid'); if (g) g.style.display = ''; }

  /* ===== NOTES EVENT WIRING ===== */
  if (notesOpenBtn) notesOpenBtn.addEventListener('click', () => openNotesEditor(), { signal: lifecycleSignal });
  if (notesSaveBtn) notesSaveBtn.addEventListener('click', () => { saveNotesFromEditor(); updateHiddenProps(); updateReadiness(); }, { signal: lifecycleSignal });
  if (notesCancelBtn) notesCancelBtn.addEventListener('click', () => { if (notesForm) notesForm.style.display = 'none'; showSavedNotePreview(); }, { signal: lifecycleSignal });
  if (notesEditBtn) notesEditBtn.addEventListener('click', () => editSavedNote(), { signal: lifecycleSignal });
  if (notesDeleteBtn) notesDeleteBtn.addEventListener('click', () => { deleteSavedNote(); updateHiddenProps(); updateReadiness(); }, { signal: lifecycleSignal });

  /* ===== INIT & RESTORE ===== */
  setupCombobox('make');
  setupCombobox('year');
  makeEditButton?.addEventListener('click', editVehicleMake, { signal: lifecycleSignal });
  if (makeSel) makeSel.addEventListener('change', onMakeChange, { signal: lifecycleSignal });
  if (yearSel) yearSel.addEventListener('change', onYearChange, { signal: lifecycleSignal });

  // Preserve state when switching metal / karat (these are anchor links that navigate away)
  try {
    const optionLinks = root.querySelectorAll('.vf__option-buttons .vf-option-btn');
    optionLinks.forEach(link => {
      link.addEventListener('click', () => {
        showGallerySlide(0);
        saveState();
      }, { signal: lifecycleSignal });
    });
  } catch (e) { /* ignore */ }

  // Fallback: save state on unload
  try { window.addEventListener('beforeunload', saveState, { signal: lifecycleSignal }); } catch (e) {}

  function restoreSavedNotes() {
    savedNotes = '';
    savedNotesData = null;
    resetNotesForm();
    try { sessionStorage.removeItem(LEGACY_NOTES_KEY); } catch (_) {}
  }

  async function initializeVehicleData({ restore = false } = {}) {
    if (registryRetryPromise) return registryRetryPromise;
    registryRetryPromise = (async () => {
      await loadPaintIndex();
      if (!requestCoordinator.isActive()) return false;
      const lockedMakeIsValid = await initializeLockedMake();
      if (lockMake && !lockedMakeIsValid) return false;
      if (restore && !initialRestoreComplete) {
        restoreSavedNotes();
        const urlState = urlStateEnabled ? configurationUrlState() : { present: false };
        if (urlState.unsupportedVersion) {
          warnInDevelopmentOnce('url-schema-version', 'Ignored an unsupported configurator URL schema version.');
        } else if (urlState.present) await applyConfigurationUrl(urlState);
        else await restoreState();
        initialRestoreComplete = true;
        syncConfigurationUrl();
      }
      if (!requestCoordinator.isActive()) return false;
      if (!lockMake && makeComboInput === document.activeElement) renderComboboxOptions('make');
      return true;
    })().catch(error => {
      if (requestCoordinator.isActive()) {
        warnInDevelopmentOnce('paint-index-load', 'Could not load the vehicle registry; a later interaction can retry.');
        if (paintUnavailable) {
          paintUnavailable.style.display = '';
          paintUnavailable.textContent = 'Vehicle and paint options could not load. Please try again, or use “don’t see your paint color listed?” above.';
        }
        updateReadiness();
      }
      return false;
    }).finally(() => {
      registryRetryPromise = null;
    });
    return registryRetryPromise;
  }

  function retryRegistryOnIntent() {
    if (!registryLoaded && requestCoordinator.isActive()) initializeVehicleData({ restore: !initialRestoreComplete });
  }

  makeComboInput?.addEventListener('focus', retryRegistryOnIntent, { signal: lifecycleSignal });
  window.addEventListener('online', retryRegistryOnIntent, { signal: lifecycleSignal });
  window.addEventListener('popstate', async () => {
    if (!urlStateEnabled || !registryLoaded || !requestCoordinator.isActive()) return;
    await applyConfigurationUrl(configurationUrlState());
  }, { signal: lifecycleSignal });

  applyRingMask();
  updateBuilderPresentation();
  shared.pdfInstances.set(root, {
    getState: pdfConfigurationSnapshot,
    renderCleanRing: () => renderConfiguredRing(1600),
    buildConfigurationUrl: () => buildCurrentConfigurationUrl().toString(),
  });
  initializeVehicleData({ restore: true }).then(() => {
    if (!requestCoordinator.isActive()) return;
    if (lockMake && makeSel.value !== defaultMake) {
      makeSel.value = defaultMake;
      syncComboboxValue('make');
    }
    showSavedNotePreview();
  });

  /* ===== UTILITIES ===== */
  function buildNotesPreviewText(data) {
    const make = data?.make || '';
    const year = data?.year || '';
    const paintLine = [data?.paintName || '', data?.paintCode || ''].filter(Boolean).join(' ');
    const lines = [
      `Make: ${make}`,
      `Year: ${year}`,
      `Paint Color Name + Code: ${paintLine}`,
    ];

    if (data?.otherDetails) {
      lines.push(`Other Details: ${data.otherDetails}`);
    }

    return lines.join('\n');
  }

  function parseNotesText(raw) {
    const lines = String(raw || '').split('\n').map((line) => line.trim());
    const make = readLabelValue(lines, ['Make:']);
    const year = readLabelValue(lines, ['Year:']);
    const paintCombo = readLabelValue(lines, ['Paint Color Name + Code:', 'Paint:']);
    const otherDetails = readMultilineLabelValue(lines, ['Other Details:']);
    const paintName = paintCombo.replace(/\s+\([^)]*\)\s*$/, '').trim() || paintCombo;
    const paintCodeMatch = paintCombo.match(/\(([^)]+)\)\s*$/);
    const paintCode = paintCodeMatch ? paintCodeMatch[1].trim() : '';
    const paint = [paintName, paintCode].filter(Boolean).join(' ').trim();

    return {
      raw: String(raw || '').trim(),
      make,
      year,
      paintName,
      paintCode,
      paint,
      otherDetails,
    };
  }

  function readLabelValue(lines, labels) {
    for (const line of lines) {
      for (const label of labels) {
        if (line.startsWith(label)) {
          return line.slice(label.length).trim();
        }
      }
    }
    return '';
  }

  function readMultilineLabelValue(lines, labels) {
    for (let index = 0; index < lines.length; index++) {
      const line = lines[index];
      for (const label of labels) {
        if (line.startsWith(label)) {
          const firstLine = line.slice(label.length).trim();
          const remaining = lines.slice(index + 1).filter(Boolean).join('\n');
          return [firstLine, remaining].filter(Boolean).join('\n').trim();
        }
      }
    }
    return '';
  }

  function escapeHtml(s) { return String(s).replace(/[&"'<>]/g, (c) => ({'&':'&amp;','"':'&quot;','\'':'&#39;','<':'&lt;','>':'&gt;'}[c])); }
  function CSSescape(v) { try { return CSS.escape(v); } catch (_) { return v; } }
  function CSSescapeSelector(v) { return v.replace(/"/g, '\\"'); }

  }

  function initializeWithin(container) {
    if (!(container instanceof Element || container instanceof Document)) return;
    if (container.matches?.('[data-vf-configurator]')) initializeConfigurator(container);
    container.querySelectorAll?.('[data-vf-configurator]').forEach(initializeConfigurator);
  }

  initializeWithin(document);

  if (!shared.lifecycleInstalled) {
    shared.lifecycleInstalled = true;
    document.addEventListener('shopify:section:load', event => initializeWithin(event.target));
    document.addEventListener('shopify:section:unload', event => {
      const roots = [];
      if (event.target instanceof Element && event.target.matches('[data-vf-configurator]')) roots.push(event.target);
      event.target?.querySelectorAll?.('[data-vf-configurator]').forEach(root => roots.push(root));
      roots.forEach(root => shared.instances.get(root)?.destroy());
    });
  }

  shared.initializeConfigurator = initializeConfigurator;
})();
